// ui.ts — the listener UI shell and state machine.
//
// States: loading -> gate (password / display name) -> [knock/pending] ->
//         live (player + DAW time + listeners + comments) | denied | error.
//
// Branding (docs §3 GET /api/sessions/:id .branding) is applied at runtime by
// setting CSS variables on the root and filling the logo slot. Placeholders are
// clearly marked so the juliusgass.de look can be dropped in per session.

import {
  getSession,
  joinSession,
  pollJoin,
  getComments,
  reportHealth,
  requestPause,
  hostKickParticipant,
  hostIntentFromUrl,
  ApiError,
  type SessionInfo,
  type Branding,
  type JoinResult,
  type CommentConfig,
} from "./api";
import { ChatVisit } from "./chat-visit";
import { Discussion } from "./discussion";
import { HostControls } from "./host-controls";
import { ListenerRoom } from "./room";
import { Room as LKRoom, RoomEvent, type LocalParticipant } from "livekit-client";
import { LosslessPlayer } from "./lossless";
import { CommentsView } from "./comments";
import { formatPosition, DEFAULT_PLAYOUT_DELAY_MS } from "./timecode";
import { loadOrCreateClientId } from "./client";

type View = "loading" | "gate" | "pending" | "denied" | "live" | "error" | "waiting";

/** Bundled brand logo (juliusgass.de mark). Vite emits + hashes this asset. */
export const BRAND_LOGO_URL = new URL("./assets/logo.svg", import.meta.url).href;

/** 2x2 black 2 s loop video (1.5 kB): the NoSleep-style screen-wake fallback
 *  for browsers whose Wake Lock API is missing or denied (iOS low-power mode,
 *  older Safari). A playing muted playsinline video keeps the display on. */
const WAKE_VIDEO_URL = new URL("./assets/wake.mp4", import.meta.url).href;

// Transport icons as inline SVG so play/pause share one visual language on every
// platform (the Unicode pair renders as text glyph vs. emoji, wildly mismatched).
const ICON_PLAY =
  '<svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true" style="vertical-align:-1px">' +
  '<path d="M2.5 1.5 L10.8 6 L2.5 10.5 Z" fill="currentColor"/></svg>';
const ICON_SPEAKER =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">' +
  '<path d="M2.6 6h2.3L8.4 3v10L4.9 10H2.6z" fill="currentColor"/>' +
  '<path class="io-wave" d="M10.6 5.6a3.3 3.3 0 0 1 0 4.8" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>' +
  '<path class="io-wave" d="M12.5 3.7a6 6 0 0 1 0 8.6" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>' +
  '<line class="io-slash" x1="2" y1="14.2" x2="14" y2="1.8" stroke="currentColor" stroke-width="1.7"/></svg>';
// The mic glyph IS the meter: while live it is drawn dim and a copy of it
// fills from the bottom with the measured level — a level display that costs
// no extra width and cannot be mistaken for decoration.
const MIC_GLYPH =
  '<rect x="6" y="1" width="4" height="8.5" rx="2" fill="currentColor"/>' +
  '<path d="M3.5 7.5v1.2a4.5 4.5 0 0 0 9 0V7.5h-1.3v1.2a3.2 3.2 0 0 1-6.4 0V7.5z" fill="currentColor"/>' +
  '<rect x="7.4" y="13" width="1.2" height="2.5" fill="currentColor"/>';
const ICON_MIC =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">' +
  '<defs><clipPath id="micfill"><rect x="0" y="16" width="16" height="0" data-mic-fill/></clipPath></defs>' +
  `<g class="mic-glyph">${MIC_GLYPH}</g>` +
  `<g class="mic-glyph mic-glyph--fill" clip-path="url(#micfill)">${MIC_GLYPH}</g>` +
  '<line class="io-slash" x1="2" y1="14.2" x2="14" y2="1.8" stroke="currentColor" stroke-width="1.7"/></svg>';
const ICON_PAUSE =
  '<svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true" style="vertical-align:-1px">' +
  '<rect x="2.2" y="1.5" width="2.7" height="9" rx="0.6" fill="currentColor"/>' +
  '<rect x="7.1" y="1.5" width="2.7" height="9" rx="0.6" fill="currentColor"/></svg>';

// ---- dB fader math (sliders are dB-LINEAR like a console fader) ----------
// ONE scale for every channel: −60…+10 dB, so unity sits at the same position
// in every row. The stream's slider ENDS at unity (boosting the mix clips);
// voices may travel above it. Slider position 0 = −∞ (true mute).
const VOICE_MIN_DB = -60;
const VOICE_MAX_DB = 10;

// Console fader taper (audio taper, not dB-linear): most of the travel lives
// around unity — 0.33 dB per step near 0 dB, widening toward the bottom where
// precision does not matter. Piecewise-linear through desk-style anchors;
// unity sits at 80 % like on a mixing desk. (dB-linear before: the whole top
// half felt like nothing happened and the bottom half did everything.)
const TAPER: ReadonlyArray<readonly [number, number]> = [
  [0, VOICE_MIN_DB], // treated as −∞ below
  [10, -40],
  [25, -25],
  [50, -10],
  [80, 0],           // unity — keep at a slider-step-exact position
  [100, VOICE_MAX_DB],
];

function sliderToDb(pos: number, _minDb: number, _maxDb: number): number | null {
  if (pos <= 0) return null; // −∞
  const p = Math.min(100, pos);
  for (let i = 1; i < TAPER.length; i++) {
    const [p0, d0] = TAPER[i - 1]!;
    const [p1, d1] = TAPER[i]!;
    if (p <= p1) return d0 + ((p - p0) / (p1 - p0)) * (d1 - d0);
  }
  return VOICE_MAX_DB;
}
function dbToGain(db: number | null): number {
  return db === null ? 0 : Math.pow(10, db / 20);
}
function gainToSlider(gain: number, _minDb: number, _maxDb: number): number {
  if (gain <= 0) return 0;
  const db = 20 * Math.log10(gain);
  if (db <= VOICE_MIN_DB) return 0;
  for (let i = 1; i < TAPER.length; i++) {
    const [p0, d0] = TAPER[i - 1]!;
    const [p1, d1] = TAPER[i]!;
    // No rounding: with step 0.1 the unity anchor (80) is slider-exact, so a
    // reset lands on 0.0 dB.
    if (db <= d1) return p0 + ((db - d0) / (d1 - d0)) * (p1 - p0);
  }
  return 100;
}
/** Console habit: double-click (or alt/option-click) a fader resets it to
 *  unity. `unity` is the slider position, not the gain. */
function wireFaderReset(el: HTMLInputElement, unity: number): void {
  const reset = () => {
    el.value = String(unity);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };
  let lastDownMs = 0;
  let suppressUntil = 0;
  // Intercept at POINTERDOWN: a range input jumps its thumb to the pointer on
  // press, so resetting afterwards looked like jump-then-snap (alt-click) and
  // a stray third click re-jumped the fader (triple-click).
  const onDown = (e: MouseEvent) => {
    const t = performance.now();
    const doubled = t - lastDownMs < 400;
    lastDownMs = t;
    if (!e.altKey && !doubled) return; // plain click/drag behaves normally
    e.preventDefault();
    e.stopPropagation();
    suppressUntil = t + 400; // swallow the compat mousedown + the click
    reset();
    el.focus();
  };
  // ONE handler per gesture: pointerdown and its compatibility mousedown both
  // ran onDown, so the second call always landed inside the double-click
  // window — every single press "double-clicked" and snapped the fader to
  // unity, which made the faders undraggable.
  let seenPointer = false;
  el.addEventListener(
    "pointerdown",
    (e) => {
      seenPointer = true;
      onDown(e as PointerEvent);
    },
    true,
  );
  el.addEventListener(
    "mousedown",
    (e) => {
      if (performance.now() < suppressUntil) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (!seenPointer) onDown(e); // only where pointer events are missing
    },
    true,
  );
  el.addEventListener(
    "click",
    (e) => {
      if (performance.now() < suppressUntil) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
  el.title = `${el.title} - double-click or alt-click to reset`;
}

function fmtDb(db: number | null): string {
  if (db === null) return "\u2212\u221e";
  // Anything that ROUNDS to zero IS zero: "−0.0 dB" at unity read as
  // "slightly turned down" and quietly undermined every other number.
  if (Math.abs(db) < 0.05) return "0.0 dB";
  const v = Math.abs(db).toFixed(1);
  return db > 0 ? `+${v} dB` : `\u2212${v} dB`;
}

interface AppOptions {
  root: HTMLElement;
  sessionId: string;
}

export class App {
  private readonly root: HTMLElement;
  private readonly sessionId: string;
  /** Per-session listener identity (docs §7.1), stable across reloads. */
  private readonly clientId: string;

  private session: SessionInfo | null = null;
  private displayName = "";
  /** Host-initiated STREAM pause (docs §7.4) — distinct from a transport stop. */
  private streamPaused = false;
  private listenerToken: string | undefined;
  /** Server-assigned palette color for this clientId (docs §7.1). */
  private ownColor: string | undefined;
  /** Streamer JWT when in HOST mode (docs §7.3) — moderate all comments. */
  private hostToken: string | undefined;
  private hostControls: HostControls | null = null;
  private discussion: Discussion | null = null;
  private communicationOpen = false;
  private commentSyncTimer: number | null = null;
  private commentSyncBusy = false;
  private hostLastSharedSec = 0;
  /** Comment moderation config (docs §7.3/§7.4). */
  private commentConfig: CommentConfig = { enabled: true, privateMode: false };
  private room: ListenerRoom | null = null;
  private lossless: LosslessPlayer | null = null;
  /** User mute + chosen output, mirrored to both the Opus element and lossless. */
  private playerMuted = false;
  private playerSinkId: string | null = null;
  /** Guards the one-time lossless bring-up (needs a user gesture). */
  private losslessStartAttempted = false;
  /** Poll timer for the pre-stream "Not streaming yet" screen (404 session). */
  private waitTimer: number | null = null;
  /** §14: this listener is call-promoted (talk bar visible, mic usable). */
  private inCall = false;
  /** §14: the participant's INTENT (wants to send). The audible state derives
   *  from it: wanted && DAW playing => auto-muted (yellow), wanted && stopped
   *  => live (gold), !wanted => off (grey). */
  private micWanted = false;
  /** Last DAW-playing value the talk logic saw (buffer-corrected). */
  private dawPlaying = false;
  /** One mic enable in flight at a time — the permission prompt keeps the
   *  promise pending for SECONDS and duplicate calls resolve to undefined,
   *  which must never be read as failure. */
  private micEnabling = false;
  /** True once a capture ACTUALLY started since the last pipeline rebuild —
   *  only then is the expensive iOS un-stick rebuild worth a brief gap. */
  private micCaptureUsed = false;
  /** Debounce for re-arming the mic after a transport pause: quick play/pause
   *  scrubbing must not flip the iOS audio session (and Bluetooth its A2DP/HFP
   *  profile) on every cycle. */
  private micArmTimer: number | null = null;
  private lastTalkState: "off" | "live" | "automuted" = "off";
  private comments: CommentsView | null = null;

  /** Screen wake lock: keep the display on during a session (iOS turns the
   *  screen off otherwise, which suspends web audio in the browser). */
  private wakeLock: { release(): Promise<void> } | null = null;
  private visibilityHooked = false;
  /** NoSleep fallback video (created on demand when the Wake Lock API fails). */
  private wakeVideo: HTMLVideoElement | null = null;
  private wakeLockUnusable = false;

  private rafHandle: number | null = null;
  private toastTimer: number | null = null;
  /** 1 Hz audio-context watchdog (iOS interruption recovery). */
  private ctxWatchTimer: number | null = null;
  private ctxStalled = false;
  /** Whether the mix track is currently arriving (LiveKit's view). */
  private mixLive = false;
  /** §20: has the mix EVER been live in this visit? Decides the wording while
   *  it is not: before the host's first Start there is nothing to pause. */
  private everStreamed = false;
  /** §16: this page joined as the session owner. */
  private isHost = false;
  /** §18: host-page listener list poll (5 s while the page is the host). */
  /** Consecutive 401/403 health polls (server forgot us → auto re-join). */
  private healthForbidden = 0;
  /** REVIEW 2.3: poll loops fire on the clock; without these latches a slow
   *  network stacks request upon request, and the very call that would notice
   *  the stall queues behind the ones that caused it. One in flight per loop. */
  private pollBusy = { health: false, waiting: false, approval: false };
  private pollTimer: number | null = null;
  private healthTimer: number | null = null;

  // Persisted UI controls.
  private playoutDelayMs = DEFAULT_PLAYOUT_DELAY_MS;
  /** Last host-chosen listener latency (ms) we applied; -1 = none yet (docs §7.7). */
  private appliedLatencyMs = -1;
  private receiverBufferMs = 100;
  private chatVisit = new ChatVisit();
  private devicesAbort: AbortController | null = null;
  /** Meter state (linear 0..1): slow-integrated RMS fill + peak-hold marker. */
  private mFillL = 0;
  private mFillR = 0;
  private mPeakL = 0;
  private mPeakR = 0;
  /** Smoothed own-mic level (0..100) for the talk button's bars. */
  private micLvl = 0;

  constructor(opts: AppOptions) {
    this.root = opts.root;
    this.sessionId = opts.sessionId;
    // docs §7.1: read/create the per-session clientId on load.
    this.clientId = loadOrCreateClientId(this.sessionId);
  }

  /** Bumped at every start(); awaited sections bail when superseded so two
   *  concurrent runs (retry double-click, waiting-poll race) can never build
   *  two live rooms (double audio) or leak an unstoppable render loop. */
  private startGen = 0;
  /** Removes the gesture-latch document listeners of the CURRENT goLive (they
   *  accumulated across every reconnect before). */
  private unhookGestureLatch: (() => void) | null = null;

  async start(): Promise<void> {
    const gen = ++this.startGen;
    // Re-entry (e.g. "Try again" after an error/disconnect) must not stack a second
    // live session on top of the old one: tear down any prior Room/CommentsView/
    // LosslessPlayer, loops and timers first, and clear the one-shot lossless latch.
    await this.teardownLive();
    if (this.waitTimer !== null) {
      window.clearInterval(this.waitTimer);
      this.waitTimer = null;
    }
    this.setView("loading");
    try {
      this.session = await getSession(this.sessionId);
      if (gen !== this.startGen) return; // superseded by a newer start()
    } catch (e) {
      if (gen !== this.startGen) return;
      // The link is shared before the first "Start streaming": the session does
      // not exist yet (404). That is not an error for the listener — show a
      // friendly waiting screen and connect automatically once it appears.
      if (e instanceof ApiError && e.status === 404) {
        this.renderWaitingForStream();
        return;
      }
      this.renderError(this.errMsg(e, "Could not load the session."));
      return;
    }
    // docs §7.3: seed the comment config from the public session object.
    if (this.session.commentConfig) this.commentConfig = this.session.commentConfig;
    // The session id doubles as the display name (shown in the browser).
    this.applyBranding(this.session.branding, this.sessionId);

    // NOTE: a session that exists but is not streaming yet no longer blocks the
    // join. People arrive before the host presses "Start streaming", and the
    // host wants to SEE who is already there (the waiting-room request,
    // 19.08.). Joining now puts them in the roster, so the host's LISTENERS
    // list fills up before a single sample has flowed; the deck shows the
    // "Waiting for the stream…" banner until it does, and audio starts by
    // itself the moment the stream comes up. Only a session that does not
    // exist AT ALL (404, above) still gets the waiting card — there is nothing
    // to join there.

    // Returning listener: join automatically with the remembered name — one
    // sign-in per browser, reconnects and reloads go straight to the player.
    // The gate still shows when a password/approval is required, when no name
    // is stored yet, or when the auto-join fails (e.g. name just got taken).
    if (hostIntentFromUrl() && localStorage.getItem("daws:host:token")) {
      try { await this.attemptJoin("Host"); return; }
      catch { /* use the existing sign-in/join recovery below */ }
    }
    const savedName = (localStorage.getItem("daws:listener:name") ?? "").trim();
    // §18: a kicked page must NOT walk right back in on its own — it lands on
    // the gate (name prefilled, one click to return). One-shot flag.
    const wasKicked = sessionStorage.getItem("daws:kicked") === "1";
    if (wasKicked) sessionStorage.removeItem("daws:kicked");
    if (savedName && !wasKicked && !this.session.requirePassword && !this.session.requireApproval) {
      try {
        await this.attemptJoin(savedName);
        return;
      } catch (e) {
        if (gen !== this.startGen) return;
        this.toast(this.errMsg(e, "Auto-join failed."));
      }
    }
    if (gen !== this.startGen) return;
    this.renderGate();
  }

  // -------------------------------------------------------------------------
  // Branding
  // -------------------------------------------------------------------------

  /**
   * Apply per-session branding (docs §3). Each property maps to a CSS variable
   * consumed by styles.css. Unset values keep the built-in placeholder theme.
   * NOTE: BRANDING PLACEHOLDER — drop in the juliusgass.de palette/logo here or
   * serve it from the session's branding object.
   */
  private applyBranding(b: Branding | undefined, sessionName: string): void {
    const rootStyle = document.documentElement.style;
    const set = (varName: string, val: string | undefined) => {
      if (val) rootStyle.setProperty(varName, val);
    };
    set("--brand-accent", b?.accent);
    set("--brand-bg", b?.bg);
    set("--brand-fg", b?.fg);
    set("--brand-surface", b?.surface);
    set("--brand-muted", b?.muted);

    const title = b?.title || sessionName || "DAW Stream";
    document.title = title;
  }

  /** Build the branded header (logo slot + title) shared by all views. */
  private headerEl(): HTMLElement {
    const header = document.createElement("header");
    header.className = "app__header";

    const logo = document.createElement("div");
    logo.className = "app__logo";
    // Per-session branding logo if set; otherwise the bundled juliusgass.de mark.
    const img = document.createElement("img");
    img.src = this.session?.branding?.logoUrl || BRAND_LOGO_URL;
    img.alt = this.session?.branding?.title || this.session?.name || "Julius Gass";
    logo.appendChild(img);

    const title = document.createElement("div");
    title.className = "app__title";
    // The session id doubles as the name; a branding title (if set) overrides it.
    title.textContent = this.session?.branding?.title || this.sessionId || "DAW Stream";

    header.append(logo, title);
    return header;
  }

  // -------------------------------------------------------------------------
  // Gate (password + display name)
  // -------------------------------------------------------------------------

  private renderGate(): void {
    this.setView("gate");
    const s = this.session!;
    this.root.innerHTML = "";
    this.root.appendChild(this.headerEl());

    const card = document.createElement("main");
    card.className = "card";

    const h = document.createElement("h1");
    h.className = "card__title";
    h.textContent = "Listen in";
    card.appendChild(h);

    const sub = document.createElement("p");
    sub.className = "card__sub";
    sub.textContent = s.live
      ? "The stream is live. Join the session."
      : "The stream is not active right now \u2014 you can still join.";
    card.appendChild(sub);

    const form = document.createElement("form");
    form.className = "form";
    form.noValidate = true;

    const nameField = labeledInput("Display name", "text", "e.g. Max");
    nameField.input.value = localStorage.getItem("daws:listener:name") ?? "";
    nameField.input.required = true;
    nameField.input.maxLength = 60;
    nameField.input.setAttribute("autocomplete", "nickname");
    form.appendChild(nameField.wrap);

    let pwField: ReturnType<typeof labeledInput> | null = null;
    if (s.requirePassword) {
      pwField = labeledInput("Password", "password", "Session password");
      pwField.input.autocomplete = "current-password";
      form.appendChild(pwField.wrap);
    }

    const note = document.createElement("p");
    note.className = "form__note";
    if (s.requireApproval) {
      note.textContent = "This session requires approval by the host.";
      form.appendChild(note);
    }

    const errEl = document.createElement("p");
    errEl.className = "form__error";
    errEl.hidden = true;
    form.appendChild(errEl);

    const btn = document.createElement("button");
    btn.type = "submit";
    btn.className = "btn btn--primary";
    btn.textContent = s.requireApproval ? "Ask to join" : "Join";
    form.appendChild(btn);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      errEl.hidden = true;
      const name = nameField.input.value.trim();
      if (!name) {
        showErr(errEl, "Please enter a display name.");
        return;
      }
      const pw = pwField?.input.value ?? undefined;
      btn.disabled = true;
      btn.textContent = "…";
      void this.attemptJoin(name, pw).catch((err) => {
        showErr(errEl, this.errMsg(err, "Join failed."));
        btn.disabled = false;
        btn.textContent = s.requireApproval ? "Ask to join" : "Join";
      });
    });

    card.appendChild(form);
    this.root.appendChild(card);
    nameField.input.focus();
  }

  private async attemptJoin(name: string, password?: string): Promise<void> {
    this.displayName = name;
    // docs §7.1: send clientId so the server assigns a stable color + rights.
    const result = await joinSession(this.sessionId, name, this.clientId, password);
    await this.handleJoinResult(result);
  }

  private async handleJoinResult(result: JoinResult): Promise<void> {
    switch (result.status) {
      case "ok":
        this.listenerToken = result.token;
        this.isHost = result.isHost === true;
        // REVIEW 1.2: this assignment was MISSING, so getHostToken() always
        // answered undefined and three host-only paths were silently dead:
        // editing and deleting other people's comments (comments.ts:425) and —
        // less obviously — the click on a comment's timecode that jumps
        // REAPER's cursor (comments.ts:538). The token only counts when the
        // SERVER confirmed us as host; an expired login yields isHost=false
        // and therefore no moderation, which is the honest outcome.
        this.hostToken = this.isHost
          ? (localStorage.getItem("daws:host:token") ?? undefined)
          : undefined;
        // Remember the name for one-tap rejoins of any future stream (the
        // TYPED name — the server's host override is not a preference).
        localStorage.setItem("daws:listener:name", this.displayName);
        // §16: the server's name is authoritative (host joins are renamed).
        if (result.displayName) this.displayName = result.displayName;
        // docs §7.1: store the server-assigned color for our own rendering.
        this.ownColor = result.color;
        if (this.isHost) this.commentConfig = { enabled: true, privateMode: false };
        await this.goLive(result.livekitUrl, result.token);
        // §16: the host sits NEXT TO the DAW — hearing the stream again,
        // half a second late, is an echo, not a feature. Start their stream
        // fader at −∞ (visible in the mixer, instantly adjustable); the call
        // voices stay at unity.
        // §16: the plugin opened us as host but the server did not accept it —
        // the browser's streamer login is gone or expired (12 h default TTL).
        if (!this.isHost && hostIntentFromUrl()) {
          const bar = this.root.querySelector<HTMLElement>('[data-role="hostbar"]');
          if (bar) bar.hidden = false;
        }
        if (this.isHost) {
          this.setPlayerVolume(0);
          // Participant management lives in HostControls alongside this room.
        }
        return;
      case "waiting":
        this.renderWaitingRoom(result.name, result.others, result.admitOneByOne);
        break;
      case "pending":
        this.renderPending(result.waitId);
        return;
      case "denied":
        this.renderDenied();
        return;
    }
  }

  // -------------------------------------------------------------------------
  // Pending (guest knock — wait for host approval)
  // -------------------------------------------------------------------------

  private renderPending(waitId: string): void {
    this.setView("pending");
    this.root.innerHTML = "";
    this.root.appendChild(this.headerEl());

    const card = document.createElement("main");
    card.className = "card card--center";
    card.innerHTML = `
      <div class="spinner" aria-hidden="true"></div>
      <h1 class="card__title">Waiting for approval…</h1>
      <p class="card__sub">The host has been notified. You'll be connected automatically once you're approved.</p>
    `;
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn btn--ghost";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => {
      this.stopPolling();
      this.renderGate();
    });
    card.appendChild(cancel);
    this.root.appendChild(card);

    // Poll the approval endpoint (docs §3 GET /api/sessions/:id/join/:waitId).
    this.stopPolling();
    const poll = async () => {
      try {
        const r = await pollJoin(this.sessionId, waitId);
        if (r.status === "pending") return; // keep waiting
        this.stopPolling();
        await this.handleJoinResult(r);
      } catch (e) {
        // Transient errors: keep polling; surface only hard ones.
        if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 0) {
          this.stopPolling();
          this.renderError(this.errMsg(e, "Could not query approval status."));
        }
      }
    };
    this.pollTimer = window.setInterval(() => {
      if (this.pollBusy.approval) return; // REVIEW 2.3: one in flight
      this.pollBusy.approval = true;
      void poll().finally(() => (this.pollBusy.approval = false));
    }, 2000);
    void poll();
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private renderDenied(): void {
    this.setView("denied");
    this.root.innerHTML = "";
    this.root.appendChild(this.headerEl());
    const card = document.createElement("main");
    card.className = "card card--center";
    card.innerHTML = `
      <h1 class="card__title">Zugang abgelehnt</h1>
      <p class="card__sub">Der Host hat deine Anfrage abgelehnt.</p>
    `;
    const back = document.createElement("button");
    back.type = "button";
    back.className = "btn btn--ghost";
    back.textContent = "Back";
    back.addEventListener("click", () => this.renderGate());
    card.appendChild(back);
    this.root.appendChild(card);
  }

  // -------------------------------------------------------------------------
  // Live (player + DAW time + listeners + comments)
  // -------------------------------------------------------------------------

  private async goLive(livekitUrl: string, token: string): Promise<void> {
    this.setView("live");
    void this.setupMediaSession(); // island/lock-screen tile (cosmetic, async)

    // Lossless is AUTOMATIC: the host picks the format in the plugin. If the host
    // is sending lossless and the browser can play it, the player flips itself on
    // (onActive → crossfade); if the host is on Opus / the connection can't hold
    // it, it stays/falls back to Opus (onInactive). No listener-side button.
    if (LosslessPlayer.isSupported() && this.listenerToken) {
      this.lossless = new LosslessPlayer({
        sessionId: this.sessionId,
        token: this.listenerToken,
        onError: (m) => this.toast(m),
        onActive: () => this.onLosslessActive(),
        onInactive: (reason) => this.onLosslessInactive(reason),
      });
      // Try to bring lossless up automatically shortly after going live (uses the
      // join click's activation, once the host latency is known for the buffer
      // match). If the browser blocks the AudioContext, the gesture latch below
      // builds and/or resumes it on the first interaction.
      window.setTimeout(() => void this.ensureLossless(), 1200);
      // Gesture latch: Safari/WebKit refuses AudioContext.resume() outside a
      // real gesture, and the auto bring-up above runs outside one — so KEEP
      // hooking gestures until the context actually runs (a `once` listener
      // could fire before the graph exists and be spent for good, leaving
      // Safari listeners on the unbuffered WebRTC fallback forever).
      const gestureEvents = ["pointerdown", "click", "keydown", "touchstart"] as const;
      const kick = () => {
        void this.ensureLossless();
        this.lossless?.resume();
        if (!this.lossless || !this.lossless.needsGesture()) {
          for (const ev of gestureEvents) document.removeEventListener(ev, kick, true);
        }
      };
      // One latch at a time: every goLive re-entry (auto-reconnect) added four
      // more permanent capture listeners before.
      this.unhookGestureLatch?.();
      this.unhookGestureLatch = () => {
        for (const ev of gestureEvents) document.removeEventListener(ev, kick, true);
      };
      for (const ev of gestureEvents) {
        document.addEventListener(ev, kick, { capture: true });
      }
    }

    this.renderLiveShell();

    this.room = new ListenerRoom(
      {
        onComment: (c) => this.comments?.addOrUpdate(c),
        onCommentResolved: (id, resolved, version) => this.comments?.applyResolved(id, resolved, version),
        onCommentEdit: (id, text) => this.comments?.applyEdit(id, text),
        onCommentDelete: (id) => this.comments?.applyDelete(id),
        onCommentConfig: (cfg) => this.setCommentConfig(cfg),
        onListeners: (count) => this.setListenerCount(count),
        onStreamState: (paused) => {
          this.applyStreamPause(paused);
          // §20: the mic is no longer gated on this (a paused stream is a
          // running conversation now), but the button state still wants a
          // re-derive on the edge.
          if (this.inCall) this.applyTalkState();
        },
        onListenerColor: (clientId, color) => this.applyListenerColor(clientId, color),
        // Instant grant push (§12/§14): Pause-DAW button + talk bar appear the
        // moment the host flips the roster toggle (the 5 s health poll made
        // that feel ~4 s laggy; it stays as reconciliation).
        onPerm: (canPause, canCall) => {
          this.setRemotePauseAllowed(canPause);
          this.setCallMode(canCall);
        },
        // §16 mini-mixer: voices appeared/left — rebuild the rows.
        onConversations: () => this.refreshMixer(),
        onConnectionChange: (connected) => this.setConnState(connected),
        onDisconnected: () => {
          // §22: a drop we asked for (the route reset below) is not a failure —
          // it reconnects itself moments later.
          if (this.intentionalRoomDrop) return;
          // Self-heal: with the remembered name, start() lands straight back in
          // the player — no error screen, no manual re-join.
          this.toast("Connection lost - reconnecting\u2026");
          void this.start();
        },
        onRemoved: () => {
          // §18 soft kick: brief notice, then a REAL reload onto the NAME
          // GATE — name prefilled, but returning takes a conscious click
          // (the flag suppresses the auto-join exactly once).
          this.toast("Disconnected");
          sessionStorage.setItem("daws:kicked", "1");
          window.setTimeout(() => window.location.reload(), 1200);
        },
        onMixState: (live) => this.setMixState(live),
        onError: (m) => this.toast(m),
      },
      this.playoutDelayMs,
      !this.isHost,
    );
    // §14 talk state, event-driven: the moment ANY local track lands (a mic
    // enable that was still in flight when the DAW started playing), re-derive
    // the state — a stale publish is released within milliseconds instead of
    // waiting for the timed backstop sweep.
    this.room.room.on(RoomEvent.LocalTrackPublished, () => this.applyTalkState());

    const commentSession = this.sessionId;
    const commentView = this.comments;
    this.commentSyncTimer = window.setInterval(() => {
      if (this.commentSyncBusy || !this.listenerToken) return;
      this.commentSyncBusy = true;
      void getComments(commentSession, { token: this.hostToken ?? this.listenerToken, clientId: this.clientId })
        .then(cs => { if (this.comments === commentView) commentView?.setInitial(cs); })
        .catch(() => {}).finally(() => { this.commentSyncBusy = false; });
    }, 5000);

    // Load any existing comments (best-effort).
    void getComments(this.sessionId, { token: this.hostToken ?? this.listenerToken, clientId: this.clientId })
      .then((cs) => this.comments?.setInitial(cs))
      .catch(() => {/* non-fatal */});

    try {
      await this.room.connect(livekitUrl, token);
    } catch (e) {
      // Tear everything down BEFORE the error screen: the proactively-built
      // LosslessPlayer would otherwise keep running and start AUDIO underneath
      // "Something went wrong".
      const msg = this.errMsg(e, "Connection to the live stream failed.");
      await this.teardownLive();
      this.renderError(msg);
      return;
    }

    void this.populateOutputDevices();
    if (this.isHost) this.setCallMode(this.communicationOpen);
    this.refreshMixer();   // stream channel exists from the start
    this.startRenderLoop();
    // Hi-fi session by default; only an actual mic capture may lift this.
    this.setAudioSessionType("playback");
    // iOS interruption watchdog: opening/closing ANY mic flips the audio
    // session and silently suspends the page's AudioContexts ("interrupted").
    // Without this, the stream stayed DEAD after using the talk mic until a
    // manual reload. Resume relentlessly; after a recovery, resync the ring
    // so playback doesn't resume seconds behind the live edge.
    if (this.ctxWatchTimer === null) {
      this.ctxWatchTimer = window.setInterval(() => {
        if (this.lossless) {
          if (!this.lossless.isCtxRunning()) {
            if (this.lossless.isActive()) this.ctxStalled = true;
            this.lossless.resume();
          } else if (this.ctxStalled) {
            this.ctxStalled = false;
            this.lossless.resync();
          }
        }
        this.room?.setSharedAudioContext(this.lossless?.getContext() ?? null);
        this.room?.resumeAudioContexts();
        // Re-assert the hi-fi session while NO capture is live (idempotent
        // property write). WebKit re-evaluates the session on route/context
        // changes and kept sliding back to the call category — the volume
        // rocker then stops controlling the stream. An event-driven pin alone
        // proved too easy to undo; a 1 Hz re-pin is not.
        if (this.lastTalkState !== "live" && !this.micEnabling)
          this.setAudioSessionType("playback");
        // §27: this interval keeps running when the tab is in the background,
        // where the render loop does not. Deriving the transport state here as
        // well is what keeps the auto-mute honest behind another tab.
        if (this.room) {
          const st = this.room.timecode.tick();
          this.syncDawPlaying(this.mixLive && !this.streamPaused && st.playing && st.hasSignal);
        }
        // §21 level-triggered pipeline rebuild: a capture ran on this page and
        // nothing is capturing now → the output is still on the OS\'s voice
        // route (mono, and on Bluetooth the telephone-band HFP link). Rebuild
        // once, from the state rather than from an edge.
        if (
          this.micCaptureUsed &&
          this.lastTalkState !== "live" &&
          !this.micEnabling &&
          !this.rebuildPending
        ) {
          this.micCaptureUsed = false;
          this.rebuildPending = true;
          window.setTimeout(() => {
            this.rebuildPending = false;
            void this.rebuildAudioPipeline();
          }, 700);
        }
        // Mix is live but no frames are arriving on the audio socket: kick it
        // rather than wait for a backoff to expire (the "20-30 s of silence
        // after the host restarted" case).
        if (this.mixLive && this.lossless && this.lossless.framesStaleMs() > 3000) {
          this.lossless.kick();
        }
      }, 1000);
    }
    // Keep the screen awake during the session + resume audio when the page
    // becomes visible again (no refresh, no re-login).
    void this.acquireWakeLock();
    this.hookVisibilityResume();

    // Periodically report our connection-health to the host (drives the plugin's
    // "is this latency adequate?" indicator). Best-effort; needs the listener token.
    if (this.healthTimer === null) {
      this.healthTimer = window.setInterval(() => {
        if (this.pollBusy.health) return; // previous poll still out (REVIEW 2.3)
        if (this.room && this.listenerToken) {
          this.pollBusy.health = true;
          // Health must describe the AUDIBLE path: while the WS player is heard,
          // the WebRTC track runs silently underneath and ITS concealment says
          // nothing about what the listener hears (a silent-path "poor" was
          // scaring the host for no reason). Worklet ring underruns are the real
          // audible metric in WS mode.
          const level = this.lossless?.isActive()
            ? this.lossless.healthLevel()
            : this.room.getConnectionHealth();
          void reportHealth(
            this.sessionId,
            this.clientId,
            level,
            this.listenerToken,
          ).finally(() => (this.pollBusy.health = false))
            .then((grants) => {
            if (!grants) return; // flaky poll: keep the last known grants
            if (grants === "forbidden") {
              // The server no longer knows this page (its in-memory roster was
              // wiped by a restart): every grant lookup fails and the page is
              // invisible in the host's lists — while audio keeps playing, so
              // nobody notices. Re-join automatically (remembered name, same
              // clientId → same color/grants; the host re-earns theirs via
              // the streamer token). Two strikes to shrug off a lone hiccup.
              this.healthForbidden += 1;
              if (this.healthForbidden >= 2) {
                this.healthForbidden = 0;
                this.toast("Re-registering\u2026");
                void this.start();
              }
              return;
            }
            this.healthForbidden = 0;
            const { canPause, canComment, canCall } = grants;
            if (typeof grants.streamPaused === "boolean") this.applyStreamPause(grants.streamPaused);
            this.setRemotePauseAllowed(canPause);
            this.setCallMode(canCall);
            // Personal comment permission (may differ from the global config a
            // late joiner seeded from GET): drives the comment UI + "You" chip.
            if (canComment !== null && canComment !== this.commentConfig.enabled) {
              this.setCommentConfig({ ...this.commentConfig, enabled: canComment });
            }
          });
        }
      }, 5000);
    }
  }

  private renderLiveShell(): void {
    this.root.innerHTML = "";
    if (this.isHost && this.hostToken) { this.renderHostShell(); return; }

    // ---- main grid: (stage later) deck + comments --------------------------
    // No separate header line: the session name + identity live inside the
    // deck (compact), and the status chip only appears when NOT live.
    const main = document.createElement("main");
    main.className = "live-main";

    // ======================= deck =======================
    const deck = document.createElement("section");
    deck.className = "deck";

    // Top row of the deck: session name left, own name right — ONE line.
    const topRow = document.createElement("div");
    topRow.className = "deck__toprow";
    const idLine = document.createElement("div");
    idLine.className = "deck__idline";
    const sess = document.createElement("div");
    sess.className = "deck__session";
    sess.textContent = this.sessionId;
    // Quality + latency are session FACTS, not controls — they belong up here
    // next to the name, not in the control row between the buttons.
    const fmt = document.createElement("div");
    fmt.className = "deck__format";
    fmt.innerHTML =
      `<span data-format="opus">Opus</span> <span data-bits>256 kbps</span> · 48 kHz · ` +
      `<button type="button" class="deck__buffer-toggle" data-latency aria-expanded="false">Buffer 100 ms</button>`;
    idLine.append(sess, fmt);
    // The NAME is the control — a separate "rename" button next to it was one
    // element too many for something you touch once. Renaming means a
    // reconnect (fresh join), so it is TWO-TAP: the first tap shows the
    // consequence, the second commits. (No window.confirm — modal dialogs
    // freeze the audio event loop, learned the hard way with comments.)
    const you = document.createElement("button");
    you.type = "button";
    you.className = "deck__you";
    you.title = "Click to use a different name (reconnects briefly)";
    const youName = (this.displayName || "Guest").replace(/</g, "&lt;");
    you.innerHTML = `<b>${youName}</b>`;
    let youArmed = false;
    you.addEventListener("click", () => {
      if (!youArmed) {
        youArmed = true;
        you.classList.add("deck__you--armed");
        you.innerHTML = `<b>rename? (reconnects)</b>`;
        window.setTimeout(() => {
          youArmed = false;
          you.classList.remove("deck__you--armed");
          you.innerHTML = `<b>${youName}</b>`;
        }, 3000);
        return;
      }
      localStorage.removeItem("daws:listener:name");
      window.location.reload();
    });
    topRow.append(idLine, you);
    deck.appendChild(topRow);
    const bufferLabel = document.createElement("label");
    bufferLabel.className = "deck__buffer";
    bufferLabel.textContent = "Listening buffer ";
    bufferLabel.hidden = true;
    bufferLabel.id = "receiver-buffer-settings";
    const bufferToggle = fmt.querySelector<HTMLButtonElement>("[data-latency]")!;
    bufferToggle.setAttribute("aria-controls", bufferLabel.id);
    bufferToggle.onclick = () => { bufferLabel.hidden = !bufferLabel.hidden; bufferToggle.setAttribute("aria-expanded", String(!bufferLabel.hidden)); };
    const bufferSelect = document.createElement("select");
    bufferSelect.setAttribute("aria-label", "Listening buffer");
    bufferSelect.title = "Automatic adapts to interruptions. Manual changes apply on the next playback or refill.";
    for (const [value, label] of [["auto", "Automatic"], ["50", "50 ms"], ["100", "100 ms"], ["250", "250 ms"],
                                 ["500", "500 ms"], ["1000", "1 s"], ["1500", "1.5 s"], ["2000", "2 s"]]) {
      bufferSelect.add(new Option(label, value));
    }
    try { bufferSelect.value = localStorage.getItem("daws:receiver-buffer") ?? "auto"; } catch {}
    if (!bufferSelect.value) bufferSelect.value = "auto";
    const applyBuffer = () => {
      this.receiverBufferMs = bufferSelect.value === "auto" ? 100 : Number(bufferSelect.value);
      bufferToggle.textContent = `Buffer ${this.receiverBufferMs} ms`;
      this.lossless?.setBufferMode(bufferSelect.value === "auto" ? null : this.receiverBufferMs);
      try { localStorage.setItem("daws:receiver-buffer", bufferSelect.value); } catch {}
    };
    bufferSelect.addEventListener("change", applyBuffer);
    applyBuffer();
    const bufferHelp = document.createElement("small");
    bufferHelp.textContent = "Changes apply at the next playback or refill.";
    bufferHelp.hidden = bufferSelect.value === "auto";
    bufferSelect.addEventListener("change", () => { bufferHelp.hidden = bufferSelect.value === "auto"; });
    bufferLabel.append(bufferSelect, bufferHelp);
    deck.append(bufferLabel);


    const tcRow = document.createElement("div");
    tcRow.className = "tc-row";
    const tcCol = document.createElement("div");
    const chipRow = document.createElement("div");
    chipRow.className = "chip-row";
    const transportChip = document.createElement("span");
    transportChip.className = "transport-chip";
    transportChip.dataset.play = "off";
    transportChip.innerHTML = `${ICON_PAUSE} PAUSED`;
    // Status chip: VISIBLE only while something is wrong/starting (CSS hides
    // the "on" state) — no permanent LIVE badge burning a header line.
    const liveChip = document.createElement("span");
    liveChip.className = "live-chip";
    liveChip.dataset.conn = "off";
    liveChip.textContent = "CONNECTING\u2026";
    // The transport chip lives in the SAME slot as the Pause-DAW button (right
    // of the clock) so swapping them never moves anything; the connection chip
    // stays here and is invisible while all is well.
    chipRow.append(liveChip);
    const clock = document.createElement("div");
    clock.className = "tc";
    // Sign in its own fixed slot so a leading "−" never shifts the digits.
    clock.innerHTML =
      `<span data-clock>` +
      `<span class="tc__sign" data-clock-sign></span>` +
      `<span data-clock-digits>00:00:00.000</span>` +
      `</span>`;
    tcCol.append(chipRow, clock);
    const tcSpacer = document.createElement("div");
    tcSpacer.className = "tc-row__spacer";
    // §12 remote pause: the ONE red control on the page (it intervenes in the
    // host's DAW). Hidden until granted; disabled while already paused.
    const pauseBtn = document.createElement("button");
    pauseBtn.type = "button";
    pauseBtn.className = "pause-daw";
    pauseBtn.dataset.remotePause = "";
    pauseBtn.dataset.play = "off";
    pauseBtn.hidden = true;
    pauseBtn.disabled = true;
    pauseBtn.innerHTML = `${ICON_PAUSE} Pause DAW`;
    pauseBtn.title = "Pause the DAW in the studio";
    pauseBtn.addEventListener("click", () => {
      if (!this.listenerToken) return;
      pauseBtn.disabled = true;
      // Debounce: re-enable ONLY if the transport is (still) playing — the
      // unconditional re-enable flipped the button back to the red "Pause DAW"
      // look one second after a successful pause.
      window.setTimeout(() => (pauseBtn.disabled = pauseBtn.dataset.play !== "on"), 1000);
      requestPause(this.sessionId, this.listenerToken).catch((e) =>
        this.toast(this.errMsg(e, "Pause failed.")),
      );
      // OPTIMISTIC mic arm: whoever presses pause is about to SPEAK, and the
      // real transport edge takes ~0.5 s to round-trip (command → REAPER →
      // tc back). Flip the local view now so a desktop mic un-mutes
      // immediately — their first word survives. If the pause fails, the
      // next tc tells the truth and the auto-mute re-engages.
      this.dawPlaying = false;
      this.applyTransportUi(false);
      if (this.inCall) this.applyTalkState();
    });
    // Grouping: "Pause DAW" is a TRANSPORT action — it belongs with the clock
    // and the PLAYING/PAUSED chip, not between the listener's own monitoring
    // controls (Mute / volume / output). The row is tall anyway, so parking it
    // right-aligned here costs no extra line.
    const transportSlot = document.createElement("div");
    transportSlot.className = "tc-row__transport";
    transportSlot.append(transportChip, pauseBtn);
    tcRow.append(tcCol, tcSpacer, transportSlot);
    deck.appendChild(tcRow);

    // Tap-to-play overlay (autoplay may be blocked until a gesture).
    const playBtn = document.createElement("button");
    playBtn.type = "button";
    // A small button was too easy to miss: a listener sat in silence without
    // knowing the browser was waiting for a click. This is now a full-width
    // banner at the very top of the deck.
    playBtn.className = "callout callout--action";
    playBtn.dataset.role = "play";
    playBtn.textContent = "\u25b6  Tap to start listening";
    playBtn.hidden = true;
    playBtn.addEventListener("click", () => {
      void this.room?.ensureAudioPlayback();
      void this.ensureLossless(); // bring lossless up on this gesture (if available)
      this.refreshPlayButton();
    });
    // Same treatment for "the host's stream is not arriving": silence with a
    // visible reason instead of a quiet chip nobody reads.
    const waitBar = document.createElement("div");
    waitBar.className = "callout callout--wait";
    waitBar.dataset.role = "waitbar";
    waitBar.textContent = "Waiting for the stream\u2026";
    waitBar.hidden = true;
    // §16: opened from the plugin but NOT recognised as host (expired login).
    // Silence here was the worst outcome: the page joined under the remembered
    // listener name with the DAW fader up, and the host only noticed by the
    // echo. Say it, and offer the one click that fixes it.
    const hostBar = document.createElement("button");
    hostBar.type = "button";
    hostBar.className = "callout callout--warn";
    hostBar.dataset.role = "hostbar";
    hostBar.textContent = "Host sign-in expired \u2014 you joined as a listener. Sign in";
    hostBar.hidden = true;
    hostBar.addEventListener("click", () => window.open("/host", "_blank", "noopener"));
    deck.append(playBtn, waitBar, hostBar);

    // Meter: stereo on desktop, one honest mono bar on touch (WebRTC audio is
    // commonly mono there and volume/sink are OS-owned anyway).
    const isTouch =
      (typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches) ||
      (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0);
    // The meter shares the MIXER's grid (label · bar · value), so the stream's
    // level bar and its fader sit on exactly the same track — one channel
    // strip instead of two unrelated widths.
    const meterRow = (ch: string, label: string) =>
      `<div class="meter__row">` +
      `<span class="meter__ch">${label}</span>` +
      `<span class="meter__track"><span class="meter__fill" data-meter-fill-${ch}></span>` +
      `<span class="meter__peak" data-meter-peak-${ch}></span></span>` +
      `<span class="meter__db" data-peak-db-${ch}>\u2212\u221e</span></div>`;
    const meter = document.createElement("div");
    meter.className = "meter";
    meter.title = "Output level (peak hold in dBFS)";
    // Stereo on EVERY device — the WS player is stereo everywhere, and a mono
    // bar on phones read like a mono stream (customer questions guaranteed).
    meter.innerHTML = meterRow("l", "L") + meterRow("r", "R");

    // Controls line: mute · volume · output · format facts (right).
    const controls = document.createElement("div");
    controls.className = "deck__controls";
    const mute = document.createElement("button");
    mute.type = "button";
    mute.className = "ctl ctl--icon ctl--speaker";
    mute.dataset.mute = "off";
    mute.innerHTML = ICON_SPEAKER;
    mute.setAttribute("aria-label", "Mute audio");
    mute.title = "Mute everything you HEAR (stream + all call voices)";
    mute.addEventListener("click", () => {
      const next = !this.playerMuted;
      this.setPlayerMuted(next);
      mute.dataset.mute = next ? "on" : "off";
      const label = next ? "Unmute audio" : "Mute audio";
      mute.setAttribute("aria-label", label);
      mute.setAttribute("aria-pressed", String(next));
      mute.title = next
        ? "Muted - click to hear the stream and the call again"
        : "Mute everything you HEAR (stream + all call voices)";
    });
    tcRow.insertBefore(mute, transportSlot);

    let outputEl: HTMLElement | null = null;
    if (!isTouch) {
      if (ListenerRoom.supportsOutputPicker()) {
        // Native picker (Chromium, secure context): includes "System default".
        const outBtn = document.createElement("button");
        outBtn.type = "button";
        outBtn.className = "ctl ctl--quiet";
        outBtn.textContent = "Output: System";
        outBtn.title = "Choose output device";
        outBtn.addEventListener("click", async () => {
          const label = await this.room?.pickOutputDevice();
          if (label) outBtn.textContent = "Output: " + label;
        });
        outputEl = outBtn;
        controls.appendChild(outBtn);
      } else if (ListenerRoom.isSecureForAudioOutput() && ListenerRoom.supportsOutputSelection()) {
        const out = document.createElement("select");
        out.className = "deck__select";
        out.dataset.output = "1";
        out.title = "Output device — click to load real device names (asks for mic permission once)";
        out.innerHTML = `<option value="__system__">Output: System</option>`;
        out.addEventListener("change", () =>
          this.setPlayerOutput(out.value === "__system__" ? "" : out.value),
        );
        out.addEventListener("focus", () => void this.unlockOutputDevices(), { once: true });
        outputEl = out;
        controls.appendChild(out);
      }
    }

    // (controls is appended below, once the talk group has joined it.)

    // §14 talk module — hidden until the host promotes this listener. ONE
    // button, three self-explaining states (off / live / muted-while-playing).
    const talkHint = document.createElement("span");
    talkHint.className = "talkgroup__hint";
    talkHint.dataset.talkHint = "";
    talkHint.hidden = true;
    const micBtn = document.createElement("button");
    micBtn.type = "button";
    micBtn.className = "ctl ctl--icon mic-main";
    micBtn.dataset.talkMic = "off";
    micBtn.dataset.talkbar = "";   // §14: shown only for promoted listeners
    micBtn.hidden = true;
    micBtn.innerHTML = ICON_MIC;
    micBtn.addEventListener("click", () => {
      this.micWanted = !this.micWanted;
      this.applyTalkState();
    });
    const micSel = document.createElement("select");
    micSel.className = "deck__select";
    micSel.dataset.talkMicSel = "";
    micSel.dataset.talkbar = "";
    micSel.hidden = true;
    micSel.title = "Microphone";
    micSel.innerHTML = `<option value="">Mic\u2026</option>`;
    micSel.addEventListener("change", () => {
      // The picker holds concrete device ids only (see fillTalkDevices).
      void this.room?.room.switchActiveDevice("audioinput", micSel.value).catch(() => {});
    });
    // ONE i/o row: what you HEAR and what you SEND, side by side —
    // [speaker] [mic] (hint) ......... [Mic ▾] [Output ▾]
    // Two pairs instead of "two tiny squares left, two wide fields right with
    // a hole between": each icon sits WITH the device it governs.
    const outPair = document.createElement("div");
    outPair.className = "io-pair io-pair--output";
    if (!outputEl) outPair.classList.add("io-pair--system");

    if (outputEl) outPair.appendChild(outputEl);
    const micPair = document.createElement("div");
    micPair.className = "io-pair";
    micPair.append(micBtn, micSel, talkHint);
    controls.append(outPair, micPair);
    deck.appendChild(controls);

    const mixer = document.createElement("div");
    mixer.className = "mixer";
    mixer.dataset.mixer = "";
    // One block: the stream's stereo meter, then every fader. No heading —
    // the "DAW" row right below already names what this is.
    const audioDetails = document.createElement("details");
    audioDetails.className = "deck__audio-details";
    audioDetails.open = !window.matchMedia("(max-width: 600px)").matches;
    const audioSummary = document.createElement("summary"); audioSummary.textContent = "Levels & mix";
    audioDetails.append(audioSummary, meter, mixer);
    deck.append(audioDetails);

    main.appendChild(deck);

    // ======================= comments =======================
    const commentsCard = document.createElement("aside");
    commentsCard.className = "comments-card";
    commentsCard.hidden = false;
    const commentsHead = document.createElement("header");
    commentsHead.className = "comments-card__head";
    commentsHead.textContent = "COMMENTS";
    // Discussion supplies the headings.
    const commentsContainer = document.createElement("div");
    commentsContainer.className = "comments-container";
    commentsCard.appendChild(commentsContainer);
    main.appendChild(commentsCard);

    this.root.appendChild(main);

    this.mountDiscussion(commentsContainer);
    this.refreshPlayButton();
  }

  private mountDiscussion(container: HTMLElement, chatRoot?: HTMLElement): void {
    this.discussion = new Discussion(container, {
      session: this.sessionId, token: () => this.listenerToken ?? this.hostToken, host: this.isHost, visit: this.chatVisit, chatRoot,
      onState: (open, canWrite, canComment) => {
        if (this.isHost) this.discussion?.setState(this.communicationOpen,canWrite);
        else this.setCommunicationState(open, canWrite, canComment);
      },
    });
    this.mountComments(this.discussion.comments);
  }

  private setCommunicationState(open: boolean, canWrite = true, canComment = true): void {
    this.communicationOpen = open;
    this.discussion?.setState(open, canWrite);
    this.setCommentConfig({ ...this.commentConfig, enabled: open && canComment });
    if (this.isHost) {
      if (this.hostControls) this.hostControls.talkContainer.hidden = !open;
      this.setCallMode(open);
      const mic = this.root.querySelector<HTMLButtonElement>("[data-talk-mic]");
      if (mic) mic.disabled = !open;
    } else if (!open) this.setCallMode(false);
  }

  private mountComments(container: HTMLElement): void {
    this.comments = new CommentsView({
      sessionId: this.sessionId,
      getToken: () => this.listenerToken,
      getAuthor: () => this.displayName || "Guest",
      getClientId: () => this.clientId,
      getOwnColor: () => this.ownColor,
      getCurrentProjectSec: () => this.isHost && (this.streamPaused || !this.mixLive) ? this.hostLastSharedSec : this.room?.timecode.currentProjectSec() ?? 0,
      getCurrentLabel: () => this.room?.timecode.currentLabel(),
      getCurrentDisp: () => this.room?.timecode.currentDisp(),
      getHostToken: () => this.hostToken,
      onError: (m) => this.toast(m),
    });
    this.comments.mount(container);
    this.comments.setConfig(this.commentConfig);
    this.comments.setSelf(this.displayName || "Guest", this.ownColor);

  }

  private renderHostShell(): void {
    const host = new HostControls(this.sessionId, this.hostToken!, this.clientId, state => {
      this.setCommunicationState(state.communicationOpen);
      if (this.mixLive !== state.sourceConnected) this.setMixState(state.sourceConnected);
      this.applyStreamPause(state.privateMonitoring || state.sourcePaused);
    }, {
      peers: () => this.room?.listConversations() ?? [],
      setGain: (id, gain) => this.room?.setConversationGain(id, gain),
    });
    this.hostControls = host;
    const nameEditor = document.createElement("details"); nameEditor.className = "control-room__host-name";
    const nameSummary = document.createElement("summary"); nameSummary.textContent = this.displayName || "Julius";
    nameSummary.title = "Change your host name";
    const nameForm = document.createElement("form");
    const nameLabel = document.createElement("label"); nameLabel.textContent = "Your name";
    const nameInput = document.createElement("input"); nameInput.value = this.displayName || "Julius";
    nameInput.required = true; nameInput.maxLength = 64; nameLabel.append(nameInput);
    const saveName = document.createElement("button"); saveName.type = "submit"; saveName.className = "btn";
    saveName.textContent = "Save & rejoin";
    nameForm.onsubmit = e => {
      e.preventDefault(); const name = nameInput.value.trim(); if (!name) return;
      localStorage.setItem("daws:host:name", name); void this.start();
    };
    nameForm.append(nameLabel, saveName); nameEditor.append(nameSummary, nameForm);
    host.element.querySelector(".control-room__nav > div:last-child")?.prepend(nameEditor);

    const controls = document.createElement("div"); controls.className = "control-room__talk-controls";
    const mic = document.createElement("button"); mic.type = "button"; mic.className = "btn mic-main";
    mic.dataset.talkMic = "off"; mic.dataset.talkbar = ""; mic.innerHTML = ICON_MIC; mic.setAttribute("aria-label", "Turn microphone on");
    mic.onclick = () => { this.micWanted = !this.micWanted; void this.room?.ensureAudioPlayback(); this.applyTalkState(); };
    const hint = document.createElement("span"); hint.dataset.talkHint = "";
    const devices = document.createElement("details"); devices.className = "control-room__devices";
    const summary = document.createElement("summary"); summary.textContent = "Audio devices";
    const panel = document.createElement("div"); panel.className = "control-room__device-panel";
    const close = document.createElement("button"); close.type = "button"; close.className = "btn";
    close.textContent = "Close"; close.setAttribute("aria-label", "Close audio devices");
    close.onclick = () => { devices.open = false; summary.focus(); };
    panel.append(close);
    devices.append(summary, panel);
    this.devicesAbort?.abort();
    this.devicesAbort = new AbortController();
    document.addEventListener("pointerdown", e => {
      if (devices.open && e.target instanceof Node && !devices.contains(e.target)) devices.open = false;
    }, {signal: this.devicesAbort.signal});
    document.addEventListener("keydown", e => {
      if (e.key === "Escape" && devices.open) { devices.open = false; summary.focus(); }
    }, {signal: this.devicesAbort.signal});
    const micLabel = document.createElement("label"); micLabel.textContent = "Microphone";
    const micSel = document.createElement("select"); micSel.dataset.talkMicSel = ""; micSel.setAttribute("aria-label", "Microphone");
    micSel.innerHTML = '<option value="">Default microphone</option>';
    micSel.onchange = () => { void this.room?.room.switchActiveDevice("audioinput", micSel.value).catch(e => this.toast(String(e))); };
    micLabel.append(micSel); panel.append(micLabel);
    if (ListenerRoom.supportsOutputSelection()) {
      const outLabel = document.createElement("label"); outLabel.textContent = "Call output";
      const out = document.createElement("select"); out.dataset.output = ""; out.setAttribute("aria-label", "Call output");
      out.innerHTML = '<option value="__system__">System output</option>';
      out.onchange = () => this.setPlayerOutput(out.value === "__system__" ? "" : out.value);
      out.onfocus = () => { void this.unlockOutputDevices(); }; outLabel.append(out); panel.append(outLabel);
    }
    const play = document.createElement("button"); play.type = "button"; play.dataset.role = "play"; play.className = "btn"; play.textContent = "Enable call audio";
    play.onclick = () => { void this.room?.ensureAudioPlayback().then(() => this.refreshPlayButton()); };
    const level = document.createElement("label"); level.className = "control-room__voice-master"; level.textContent = "Guest voices";
    const slider = document.createElement("input"); slider.type = "range"; slider.min = "0"; slider.max = "100"; slider.value = String(this.voiceMaster * 100);
    slider.setAttribute("aria-label", "Guest voices listening volume"); slider.oninput = () => { this.voiceMaster = Number(slider.value) / 100; this.applyOutputVolume(); }; level.append(slider);
    controls.append(mic, hint, devices, play); host.talkContainer.append(controls, level);
    this.root.append(host.element); this.mountDiscussion(host.commentsContainer, host.chatContainer); this.refreshPlayButton();
  }

  /**
   * Reflect a comment-config change (docs §7.3/§7.4): seed from GET or apply a
   * live ctrl comment_config packet. Pushes it into the CommentsView which
   * hides/disables the input (with a hint) and filters in private mode.
   */
  private applyStreamPause(paused: boolean): void {
    if (this.streamPaused === paused) return;
    this.streamPaused = paused;
    this.lossless?.resync();
    this.applyOutputVolume();
    if (paused) this.syncDawPlaying(false);
  }

  private setCommentConfig(config: CommentConfig): void {
    config = { ...config, enabled: config.enabled && this.communicationOpen };
    if (this.isHost) config.privateMode = false;
    const changed = config.enabled !== this.commentConfig.enabled || config.privateMode !== this.commentConfig.privateMode;
    this.commentConfig = config;
    if (changed) this.comments?.setConfig(config);
    // No comments allowed -> the whole card disappears (the deck then takes the
    // full grid width via the :has() rule in styles.css).
    const card = this.root.querySelector<HTMLElement>(".comments-card");
    if (card) card.hidden = !this.communicationOpen;
  }

  /**
   * Apply a host color override (docs §7.4 listener_color): recolor that
   * clientId's comments, and if it targets our own clientId update the me-chip +
   * the color used for newly authored comments.
   */
  private applyListenerColor(clientId: string, color: string): void {
    this.comments?.applyColor(clientId, color);
    if (clientId === this.clientId) {
      this.ownColor = color;
      // Self-identity lives at the comment input now (name + color dot).
      this.comments?.setSelf(this.displayName, color);
    }
  }

  // ---- Player controls routed to BOTH paths (only the active one is audible) --

  /** User fader (0..1). The audible value is this times the talk duck. */
  private playerVolume = 1;
  /** User master for the CALL voices (per-person faders stack on top). The
   *  one volume that always works on iPhones: while any capture is open, iOS
   *  fixes WebRTC playback volume and ignores the hardware rocker entirely —
   *  an in-page fader is the only control the page can actually honor. */
  private voiceMaster = 1;
  /** Per-person fader rows unfolded? Collapsed on EVERY connect (host wish:
   *  two faders is the simple default; the presence line keeps the overview).
   *  Not persisted — expanding is a within-session move. */
  private voicesOpen = false;
  /** Anti-feedback duck — VOICES ONLY. The DAW stream is NEVER ducked: it is
   *  the product (host decree 2026-08-14, after a release bug left the music
   *  13 dB down for a whole session). While the user speaks, the OTHER
   *  people's voices sit back so the loop mic → far speaker → far mic loses
   *  gain; the music plays through at unity regardless. */
  private duckFactor = 1;
  // 2026-08-14, final: NO ducking (host decree, after −15 → −9 → −5 dB all
  // still read as "his level swings"). Voices always play at face value;
  // feedback control = echo cancellation + headphones. The machinery stays
  // (a future per-user toggle can revive it by setting this below 1).
  private static readonly TALK_DUCK = 1; // OFF — no automatic dip

  /** How long the page stays COMPLETELY silent after a capture ends, so iOS
   *  can deactivate the audio session and pull a Bluetooth sink back up from
   *  the mono HFP link to A2DP. */
  private static readonly BT_ROUTE_SETTLE_MS = 1200;

  /** Speech gate: RMS above this counts as "talking" (≈ −40 dBFS). */
  private static readonly TALK_GATE = 0.01;

  private setPlayerVolume(v: number): void {
    this.playerVolume = v;
    this.applyOutputVolume();
  }


  private applyOutputVolume(): void {
    // STREAM: the user fader, nothing else — full level always.
    // VOICES: user master × talk duck.
    const stream = this.streamPaused ? 0 : this.playerVolume;
    this.room?.setVolume(stream);
    this.lossless?.setVolume(stream);
    this.room?.setConversationMaster(this.voiceMaster * this.duckFactor);
  }


  /** Move the duck toward its target with a fast attack / slow release, so it
   *  neither pumps on syllables nor clamps the far side after you stop. */
  private stepTalkDuck(speaking: boolean): void {
    const target = speaking ? App.TALK_DUCK : 1;
    const rate = target < this.duckFactor ? 0.35 : 0.1; // attack : release (~0.5 s back up)
    const next = this.duckFactor + (target - this.duckFactor) * rate;
    if (Math.abs(next - this.duckFactor) < 0.005 && Math.abs(next - target) < 0.01) {
      if (this.duckFactor === target) return;
      this.duckFactor = target;
      this.applyOutputVolume();
      return;
    }
    this.duckFactor = next;
    this.applyOutputVolume();
  }

  /** Pin WebKit's audio session (iOS 16.4+, no-op elsewhere). "playback" =
   *  the hi-fi MediaPlayback session — set it whenever NO capture is live so
   *  the output deterministically snaps back after mic use (research: the
   *  categoryOverride is checked BEFORE the capture branch in WebKit, and
   *  "playback" also survives the hardware mute switch). "auto" hands control
   *  back to WebKit right before we open a mic — a capture under a pinned
   *  "playback" category would fail. */
  /** iOS Dynamic Island / lock screen: without MediaSession metadata the OS
   *  falls back to the favicon — a transparent SVG on the system's WHITE
   *  tile, i.e. invisible. Compose a 512² artwork at runtime instead: the
   *  brand logo centered on the brand's dark card color. */
  private async setupMediaSession(): Promise<void> {
    try {
      if (!("mediaSession" in navigator)) return;
      const img = new Image();
      img.src = BRAND_LOGO_URL;
      await img.decode();
      const c = document.createElement("canvas");
      c.width = c.height = 512;
      const g = c.getContext("2d");
      if (!g) return;
      g.fillStyle = "#17171d"; // deck card tone — the island tile is opaque
      g.fillRect(0, 0, 512, 512);
      const pad = 104;
      const s = Math.min((512 - 2 * pad) / img.width, (512 - 2 * pad) / img.height);
      const w = img.width * s;
      const h = img.height * s;
      g.drawImage(img, (512 - w) / 2, (512 - h) / 2, w, h);
      const blob = await new Promise<Blob | null>((r) => c.toBlob(r, "image/png"));
      if (!blob) return;
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.sessionId,
        artist: "DAW Stream",
        artwork: [{ src: URL.createObjectURL(blob), sizes: "512x512", type: "image/png" }],
      });
    } catch {
      /* cosmetic — never let artwork break playback */
    }
  }

  private setAudioSessionType(t: "playback" | "auto"): void {
    try {
      const s = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
      if (s && s.type !== t) s.type = t;
    } catch {
      /* not exposed — fine */
    }
  }

  /** Best-effort screen wake lock (supported on iOS 16.4+/Chrome). Re-acquired
   *  every time the page becomes visible (the OS releases it on hide). */
  private async acquireWakeLock(): Promise<void> {
    try {
      const wl = (navigator as unknown as { wakeLock?: { request(t: string): Promise<{ release(): Promise<void> }> } }).wakeLock;
      if (!wl?.request) {
        this.wakeLockUnusable = true; // no API — the fallback video takes over
        return;
      }
      void this.wakeLock?.release().catch(() => {}); // drop a stale lock first
      const lock = await wl.request("screen");
      this.wakeLock = lock;
      // iOS releases the lock on its own now and then (route changes, system
      // dialogs) WITHOUT a visibilitychange — re-arm as soon as that happens.
      (lock as unknown as { addEventListener?: (t: string, f: () => void) => void })
        .addEventListener?.("release", () => {
          if (this.wakeLock !== lock) return; // superseded by a newer lock
          this.wakeLock = null;
          if (!document.hidden) window.setTimeout(() => void this.acquireWakeLock(), 400);
        });
    } catch {
      // Denied (iOS low-power mode etc.) — remember it so the next user
      // gesture starts the NoSleep fallback video instead.
      this.wakeLockUnusable = true;
    }
  }

  /** Screen-wake FALLBACK: a playing muted playsinline loop video keeps iOS
   *  from locking the screen when the Wake Lock API is unavailable/denied.
   *  Must be (re)started from a user gesture. */
  private ensureWakeVideo(): void {
    if (!this.wakeVideo) {
      const v = document.createElement("video");
      v.src = WAKE_VIDEO_URL;
      v.muted = true;
      v.loop = true;
      v.setAttribute("playsinline", "");
      v.setAttribute("aria-hidden", "true");
      v.style.cssText =
        "position:fixed;right:0;bottom:0;width:2px;height:2px;opacity:0.01;pointer-events:none;";
      document.body.appendChild(v);
      this.wakeVideo = v;
    }
    if (this.wakeVideo.paused) void this.wakeVideo.play().catch(() => {});
  }

  /** Coming back from background/lock: re-arm the wake lock and resume audio
   *  WITHOUT a refresh (the WS player re-prebuffers by itself; WebRTC stays the
   *  silent floor). */
  private hookVisibilityResume(): void {
    if (this.visibilityHooked) return;
    this.visibilityHooked = true;
    // Belt for iOS: ANY tap re-kicks a context that WebKit suspended behind our
    // back (audio-session interruptions surface as a silently stuck graph).
    document.addEventListener(
      "pointerdown",
      () => {
        // QUIET when healthy: unconditional startAudio()/resume() churn on
        // EVERY tap audibly ticked the live worklet output on iOS (the "tiny
        // glitch when pausing via the stream page" — the tap, not the pause).
        if (this.lossless && !this.lossless.isCtxRunning()) this.lossless.resume();
        if (this.room && !this.room.canPlayback) void this.room.ensureAudioPlayback();
        // iOS denies wake-lock requests outside a gesture (our auto-rejoin has
        // none) and drops held locks on its own — re-arm whenever we lack one.
        if (!this.wakeLock) void this.acquireWakeLock();
        // Wake Lock API unusable → the NoSleep fallback video needs THIS
        // gesture to start (and to restart after iOS paused it).
        if (this.wakeLockUnusable) this.ensureWakeVideo();
      },
      { capture: true },
    );
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) return;
      void this.acquireWakeLock();
      if (this.wakeLockUnusable) this.ensureWakeVideo();
      this.lossless?.resume();
      void this.room?.ensureAudioPlayback();
      // §27: coming back to the front, re-derive at once instead of waiting
      // for the next watchdog tick — the transport may have moved while the
      // render loop was asleep.
      if (this.room) {
        const st = this.room.timecode.tick();
        this.syncDawPlaying(this.mixLive && !this.streamPaused && st.playing && st.hasSignal);
      }
    });
  }

  private setPlayerMuted(m: boolean): void {
    this.playerMuted = m;
    // Show the reach of the master mute (stream AND voices) by dimming the
    // mixer AND the meter — no label needed, the red struck speaker names
    // the cause. (The meter keeps moving: the DAW is still playing, you just
    // don't hear it — dim, not frozen, states exactly that.)
    for (const el of this.root.querySelectorAll<HTMLElement>("[data-mixer], .deck .meter"))
      el.dataset.muted = m ? "1" : "0";
    this.room?.setMuted(m);
    this.lossless?.setMuted(m);
  }

  private setPlayerOutput(deviceId: string): void {
    this.playerSinkId = deviceId;
    void this.room?.setOutputDevice(deviceId);
    void this.lossless?.setOutputDevice(deviceId);
  }

  /** Bring the lossless path up (build graph + open the WS) once, from a user
   *  gesture (the AudioContext needs one). The watchdog then decides whether it
   *  actually becomes audible (host sending it) via onActive/onInactive. */
  /** Each receiver owns its buffer; the host no longer imposes a delay. */
  private wsRingTargetMs(): number {
    return this.lossless?.getTargetMs() ?? this.receiverBufferMs;
  }

  private async ensureLossless(): Promise<void> {
    if (this.isHost) return; // The control room monitors the DAW locally.
    if (this.losslessStartAttempted || !this.lossless) return;
    this.losslessStartAttempted = true;
    try {
      const target = this.wsRingTargetMs();
      this.lossless.setTargetMs(target);
      if (this.playerSinkId) void this.lossless.setOutputDevice(this.playerSinkId);
      await this.lossless.enable();
      // Call voices ride the player's (unlocked, running) context from now on.
      this.room?.setSharedAudioContext(this.lossless.getContext());
      // Learn this device's healthy output width from the widest context it
      // has ever produced. Degradation only ever NARROWS it, so the maximum is
      // the honest baseline — and a genuinely mono device never triggers the
      // retry rebuilds below, because its maximum is 1.
      const ch = this.lossless.outputChannels();
      if (ch > this.healthyOutChannels) this.healthyOutChannels = ch;
    } catch {
      this.losslessStartAttempted = false; // allow a retry on a later gesture
    }
  }

  /** WS player became the audible output → hand over HARD. No overlapping
   *  timers: the old 120 ms crossfade timers could interleave when the watchdog
   *  flapped, leaving BOTH paths audible (audible phasing — the two paths play
   *  the same audio at slightly different positions). The render tick below
   *  additionally enforces the suppression every frame, so a stray fade can
   *  never resurrect the WebRTC element while the WS player is active. */
  private onLosslessActive(): void {
    if (!this.lossless) return;
    this.room?.suppressForLossless(true); // WebRTC element hard-silent NOW
    this.lossless.fadeIn(120);            // soft edge only on the WS side
  }

  /** WS player stopped/degraded → back to the WebRTC floor. */
  private onLosslessInactive(_reason: string): void {
    void this.room?.ensureAudioPlayback();
    this.lossless?.fadeOut(120);
    this.room?.suppressForLossless(false);
  }

  /** §16 mini-mixer: rebuild the per-voice gain rows (0–200 %). */
  private refreshMixer(): void {
    if (this.isHost) { this.hostControls?.refreshAudio(); return; }
    const box = this.root.querySelector<HTMLElement>("[data-mixer]");
    if (!box || !this.room) return;
    const room = this.room;
    box.innerHTML = "";

    // ONE mixer: the stream is simply the first channel, identical in
    // geometry to every voice below it (label · meter · fader · dB). Only the
    // range differs — the mix is capped at unity (0 dB), voices may be lifted.
    const addRow = (
      label: string,
      isStream: boolean,
      levelKey: string,
      value: number,
      onDb: (db: number | null) => void,
      muted = false,
    ) => {
      // ONE scale for every channel (−60…+10 dB) so 0 dB sits at the SAME
      // position in every row — console habit. The stream simply cannot travel
      // past unity: its slider ends there (boosting the mix would clip).
      const minDb = VOICE_MIN_DB;
      const maxDb = VOICE_MAX_DB;
      const unityPos = gainToSlider(1, minDb, maxDb);
      const row = document.createElement("div");
      row.className = isStream
        ? "mixer__row mixer__row--stream"
        : muted
          ? "mixer__row mixer__row--muted"
          : "mixer__row";
      const name = document.createElement("span");
      name.className = "mixer__name";
      name.textContent = muted && !isStream ? `${label} · muted` : label;
      // No meter COLUMN any more: a blank cell in the stream row read like a
      // rendering bug. A talker's level lives on the dot in front of their
      // name (presence + level in one), the stream needs none — the big L/R
      // meter above is its display.
      const dot = document.createElement("span");
      dot.className = "mixer__dot";
      if (!isStream) {
        dot.dataset.voiceDot = levelKey;
        if (muted) dot.dataset.muted = "1";
      } else {
        dot.dataset.none = "1";
      }
      const slider = document.createElement("input");
      slider.type = "range";
      slider.min = "0";
      // SAME min/max on every row — capping the range would rescale the track
      // and move unity to the end again (the very misalignment we are fixing).
      // The stream is limited by clamping its VALUE at unity below, so its
      // thumb stops exactly on the shared 0 dB line.
      slider.max = "100";
      slider.step = "0.1";
      slider.value = String(gainToSlider(value, minDb, maxDb));
      slider.className = "deck__volume mixer__gain";
      slider.title = isStream
        ? "Stream volume (0 dB max - unity, no boost)"
        : `${label} volume (\u2212\u221e to +10 dB)`;
      wireFaderReset(slider, unityPos); // unity = 0 dB, same x in every row
      const pct = document.createElement("span");
      pct.className = "mixer__pct";
      pct.textContent = fmtDb(sliderToDb(Number(slider.value), minDb, maxDb));
      slider.addEventListener("input", () => {
        if (isStream && Number(slider.value) > unityPos) slider.value = String(unityPos);
        const db = sliderToDb(Number(slider.value), minDb, maxDb);
        pct.textContent = fmtDb(db);
        onDb(db);
      });
      const faderWrap = document.createElement("span");
      faderWrap.className = isStream ? "faderwrap faderwrap--capped" : "faderwrap";
      faderWrap.appendChild(slider);
      const labelCell = document.createElement("span");
      labelCell.className = "mixer__label";
      labelCell.append(dot, name);
      row.append(labelCell, faderWrap, pct);
      box.appendChild(row);
      return row;
    };

    // "Music" / "Voices": both rows name what you HEAR — "DAW" was host
    // jargon, "Call" was ambiguous (one listener read it as their own name).
    addRow("Music", true, "", room.getVolume(), (db) => this.setPlayerVolume(dbToGain(db)));
    const convs = room.listConversations();
    // "Voices" — master over ALL voices, unity-capped like the DAW row
    // (boosting individuals is what the per-person faders are for). The
    // per-person rows fold away underneath it; COLLAPSED the row itself lists
    // who is in the call, so folding costs no presence overview.
    if (convs.length > 0) {
      const vrow = addRow("Voices", true, "", this.voiceMaster, (db) => {
        this.voiceMaster = dbToGain(db);
        this.applyOutputVolume();
      });
      // The LABEL is the fold control — chevron inline after the word, the
      // whole cell clickable. (A separate button column indented "Voices"
      // against every other row name; this keeps the left edge straight.)
      const labelCell = vrow.querySelector<HTMLElement>(".mixer__label");
      const nameEl = vrow.querySelector<HTMLElement>(".mixer__name");
      if (labelCell && nameEl) {
        labelCell.classList.add("mixer__label--fold");
        labelCell.title = this.voicesOpen
          ? "Hide the per-person faders"
          : "Show one fader per person";
        const chev = document.createElement("span");
        chev.className = "mixer__chev";
        chev.textContent = this.voicesOpen ? "\u25be" : "\u25b8";
        nameEl.appendChild(chev);
        const toggle = () => {
          this.voicesOpen = !this.voicesOpen;
          this.refreshMixer();
        };
        labelCell.addEventListener("click", toggle);
        if (!this.voicesOpen) {
          // Presence gets a full-width line of its own under the row — inside
          // the 148 px label column the names truncated after one entry,
          // which defeated the whole "fold without losing the overview" idea.
          const folks = document.createElement("div");
          folks.className = "mixer__present";
          folks.textContent = convs
            .map((c) => (c.muted ? `${c.label} (muted)` : c.label))
            .join(" \u00b7 ");
          folks.title = "Show one fader per person";
          folks.addEventListener("click", toggle);
          box.appendChild(folks);
        }
      }
    }
    if (this.voicesOpen) {
      for (const { id, label, muted } of convs) {
        // §23: a voice that is running seconds behind is not a mystery any
        // more — say so in its own row. Below ~400 ms nobody notices, so
        // nothing is shown; above it, the number explains the conversation
        // that "felt like several seconds" instead of leaving it to a hunch.
        const delayMs = room.conversationDelayMs(id);
        const shownLabel =
          delayMs > 400 ? `${label} · +${(delayMs / 1000).toFixed(1)} s` : label;
        const prow = addRow(
          shownLabel,
          false,
          id,
          room.getConversationGain(id),
          (db) => room.setConversationGain(id, dbToGain(db)),
          muted,
        );
        // §18: the host removes people from right here — same soft kick as
        // the plugin ✕ (disconnect now, no ban, reload rejoins). Two-tap.
        if (this.isHost) {
          const kick = document.createElement("button");
          kick.type = "button";
          kick.className = "mixer__kick";
          kick.textContent = "\u2715";
          kick.title = `Remove ${label} (they can rejoin by reloading)`;
          let armed = false;
          kick.addEventListener("click", () => {
            if (!armed) {
              armed = true;
              kick.textContent = "sure?";
              kick.classList.add("mixer__kick--armed");
              window.setTimeout(() => {
                armed = false;
                kick.textContent = "\u2715";
                kick.classList.remove("mixer__kick--armed");
              }, 3000);
              return;
            }
            const hostToken = localStorage.getItem("daws:host:token");
            if (!hostToken) return;
            kick.disabled = true;
            hostKickParticipant(this.sessionId, { identity: id }, hostToken).catch(() => {
              kick.disabled = false;
              this.toast(`Could not remove ${label}.`);
            });
          });
          prow.appendChild(kick);
        }
      }
    }
  }

  /** Fill the output-device <select> once the room exists (labels need permission). */
  private async populateOutputDevices(): Promise<void> {
    if (!this.room || !ListenerRoom.supportsOutputSelection()) return;
    const sel = this.root.querySelector<HTMLSelectElement>("[data-output]");
    if (!sel) return;
    const devs = await this.room.listOutputDevices();
    if (devs.length === 0) {
      sel.hidden = true;
      return;
    }
    sel.innerHTML = `<option value="__system__">Output: System</option>`;
    devs.forEach((d, i) => {
      if (d.deviceId === "default") return; // covered by the System option
      const o = document.createElement("option");
      o.value = d.deviceId;
      o.textContent = d.label || `Output device ${i + 1}`;
      sel.appendChild(o);
    });
  }

  /** Grant a one-time device permission to reveal real output-device names, then
   *  repopulate the <select> (preserving the current selection). */
  private async unlockOutputDevices(): Promise<void> {
    if (!this.room) return;
    const sel = this.root.querySelector<HTMLSelectElement>("[data-output]");
    if (!sel) return;
    const devs = await this.room.unlockOutputLabels();
    if (devs.length === 0) return;
    const current = sel.value;
    sel.innerHTML = `<option value="__system__">Output: System</option>`;
    devs.forEach((d, i) => {
      if (d.deviceId === "default") return;
      const o = document.createElement("option");
      o.value = d.deviceId;
      o.textContent = d.label || `Output device ${i + 1}`;
      sel.appendChild(o);
    });
    if (current) sel.value = current;
  }

  private startRenderLoop(): void {
    const tick = () => {
      if (this.room) {
        // EXACTLY ONE audible path, enforced every frame: while the WS player is
        // heard the WebRTC element is pinned silent (and un-pinned otherwise).
        // Idempotent + cheap; makes phasing (both paths audible) impossible even
        // if some fade/timer ever races.
        // The WS path may only silence the WebRTC floor while it is REALLY
        // rendering: a built-but-suspended context (autoplay policy before the
        // first gesture, iOS interruption) used to mute WebRTC while producing
        // nothing itself — the listener heard NOTHING until they happened to
        // click something ("first play was silent, after stop+play it worked").
        const wsAudible =
          (this.lossless?.isActive() ?? false) && (this.lossless?.isCtxRunning() ?? false);
        this.room.suppressForLossless(wsAudible);
        // While the WS player is what the listener hears, the clock backs off
        // by the WS chain's real depth (ring target + ~transit/output) — the
        // muted WebRTC receiver's jitter stats no longer poison the estimate.
        this.room.timecode.setPlayoutOverrideMs(wsAudible ? (this.lossless?.getDepthMs() ?? 100) + 30 : null);
        this.room.timecode.setAudibleSample(wsAudible ? this.lossless?.getAudibleSample() ?? null : null);
        // A stuck WS context must surface its tap-to-play affordance promptly.
        this.refreshPlayButton();

        const st = this.room.timecode.tick();
        const receiverTarget = this.wsRingTargetMs();
        if (receiverTarget !== this.appliedLatencyMs) {
          this.appliedLatencyMs = receiverTarget;
          this.playoutDelayMs = receiverTarget;
          this.room.timecode.setBasePlayoutDelayMs(receiverTarget);
          this.room.setJitterBufferTarget(receiverTarget);
        }
        this.lossless?.setSourcePlaying(this.mixLive && !this.streamPaused && st.playing && st.hasSignal);

        // Level meter: a smooth, slowly-integrated RMS fill + a peak-hold marker.
        // dB scale (−60..0 dB → 0..100%) reads like a real meter; runs in every state.
        const lv = this.room.getLevels();
        const FILL = 0.12; // RMS integration per frame (low = calm, longer average)
        const REL = 0.991; // peak-hold release per frame (slow fall-back)
        this.mFillL += ((lv ? lv.l.rms : 0) - this.mFillL) * FILL;
        this.mFillR += ((lv ? lv.r.rms : 0) - this.mFillR) * FILL;
        this.mPeakL = Math.max(lv ? lv.l.peak : 0, this.mPeakL * REL);
        this.mPeakR = Math.max(lv ? lv.r.peak : 0, this.mPeakR * REL);
        const dbPct = (lin: number): number => {
          if (lin <= 0.0001) return 0;
          const db = 20 * Math.log10(lin);
          const p = ((db + 60) / 60) * 100; // −60 dB → 0%, 0 dB → 100%
          return p < 0 ? 0 : p > 100 ? 100 : p;
        };
        const setMeter = (ch: string, fill: number, peak: number) => {
          const f = this.root.querySelector<HTMLElement>(`[data-meter-fill-${ch}]`);
          const p = this.root.querySelector<HTMLElement>(`[data-meter-peak-${ch}]`);
          const peakPct = dbPct(peak);
          if (f) f.style.width = `${dbPct(fill)}%`;
          if (p) {
            p.style.left = `${peakPct}%`;
            // Hot peaks (near 0 dBFS) flush red; otherwise the bright accent.
            p.style.background = peakPct >= 96 ? "#ff453a" : "#e2c97e";
          }
        };
        setMeter("l", this.mFillL, this.mPeakL);
        setMeter("r", this.mFillR, this.mPeakR);
        // Numeric peak hold — a meter without a scale is decoration.
        const peakDb = (lin: number): string =>
          lin <= 0.0001 ? "\u2212\u221e" : fmtDb(20 * Math.log10(Math.min(1, lin)));
        const pl = this.root.querySelector<HTMLElement>("[data-peak-db-l]");
        const pr = this.root.querySelector<HTMLElement>("[data-peak-db-r]");
        if (pl) pl.textContent = peakDb(this.mPeakL);
        if (pr) pr.textContent = peakDb(this.mPeakR);

        // §17 live levels: own mic (button bars) + one bar per talking
        // participant (mixer rows). RMS → dB → bar height/width, smoothed by
        // the same integration the mix meter uses so it reads calmly.
        {
          const lvlPct = (rms: number): number => {
            if (rms <= 0.0005) return 0;
            const db = 20 * Math.log10(rms);
            const p = ((db + 50) / 50) * 100; // −50 dB → 0 %, 0 dB → 100 %
            return p < 0 ? 0 : p > 100 ? 100 : p;
          };
          const fill = this.root.querySelector<SVGRectElement>("[data-mic-fill]");
          if (fill) {
            const live =
              fill.closest("[data-talk-mic]")?.getAttribute("data-talk-mic") === "live";
            const rms = live ? this.room.micLevel() : 0;
            const p = live ? lvlPct(rms) : 0;
            this.micLvl += (p - this.micLvl) * 0.35;
            // Anti-feedback: duck only WHILE SPEAKING (see TALK_DUCK). Step
            // EVERY frame — releasing only while live was the bug that left
            // the duck stuck at full depth once the mic closed.
            this.stepTalkDuck(live && rms > App.TALK_GATE);
            const h = (this.micLvl / 100) * 16; // glyph height in viewBox units
            fill.setAttribute("y", String(16 - h));
            fill.setAttribute("height", String(h));
          }
          const dots = this.root.querySelectorAll<HTMLElement>("[data-voice-dot]");
          dots.forEach((el) => {
            const id = el.dataset.voiceDot ?? "";
            const p = this.playerMuted ? 0 : lvlPct(this.room!.conversationLevel(id));
            const prev = Number(el.dataset.lvl ?? "0");
            const next = prev + (p - prev) * 0.35;
            el.dataset.lvl = String(next);
            // Presence + level in one mark: it glows and grows while talking.
            el.style.opacity = String(0.32 + (next / 100) * 0.68);
            el.style.transform = `scale(${1 + (next / 100) * 0.5})`;
          });
        }

        const clockEl = this.root.querySelector<HTMLElement>("[data-clock]");
        const signEl = this.root.querySelector<HTMLElement>("[data-clock-sign]");
        const digitsEl = this.root.querySelector<HTMLElement>("[data-clock-digits]");
        // Transport elements = the status badge AND the §12 pause button; which ONE
        // is present is decided by the grant swap (the `hidden` attribute), while
        // the host's stream-pause hides whichever is active via inline display —
        // two orthogonal switches that must not fight over the same attribute.
        const transportEls = this.root.querySelectorAll<HTMLElement>("[data-play]");
        const setTransportVisible = (v: boolean) =>
          transportEls.forEach((el) => (el.style.display = v ? "" : "none"));

        // Split a leading sign into the fixed-width slot so the digits never shift.
        const setClock = (text: string) => {
          const neg = /^[-−]/.test(text);
          if (signEl) signEl.textContent = neg ? "−" : "";
          if (digitsEl) digitsEl.textContent = neg ? text.slice(1) : text;
        };

        // §20: no mix on the line means no position. The host's Stop IS the
        // pause button (the call keeps running), so the deck must say which of
        // the two silences this is: nothing has started yet, or the host
        // stepped away from the transport. Everything else — mics, chat,
        // levels — carries on either way.
        if (this.isHost && this.mixLive && !this.streamPaused && st.hasSignal) this.hostLastSharedSec = this.room?.timecode.currentProjectSec() ?? this.hostLastSharedSec;
        if (this.isHost && (this.streamPaused || (!this.mixLive && this.everStreamed))) {
          setClock(formatPosition(this.hostLastSharedSec, this.room?.timecode.currentDisp()));
          this.syncDawPlaying(false);
          this.rafHandle = requestAnimationFrame(tick); return;
        }
        if (!this.mixLive || this.streamPaused) {
          const started = this.everStreamed || st.hasSignal;
          if (clockEl) {
            setClock(started ? "Stream paused" : "Not streaming yet");
            clockEl.classList.add("clock__value--paused");
            clockEl.classList.toggle("clock__value--idle", !started);
            clockEl.classList.remove("clock__value--label");
          }
          setTransportVisible(false);
          // §25: the §14 talkback rule lives BELOW this early return, so a
          // stream that ended mid-playback froze dawPlaying at "playing" and
          // the auto-muted microphone never reopened — "sprechen ist nicht
          // möglich" (host, 22.08.). No stream means no music, so the
          // auto-mute has nothing left to mute for: release it here.
          if (this.dawPlaying) {
            this.dawPlaying = false;
            this.applyTalkState();
          }
          this.rafHandle = requestAnimationFrame(tick);
          return;
        }
        if (clockEl) clockEl.classList.remove("clock__value--idle");
        if (clockEl) clockEl.classList.remove("clock__value--paused");
        setTransportVisible(true);

        if (clockEl) {
          // docs §7.7: format the buffer-corrected position in the host's chosen
          // unit so the readout stays aligned with the heard audio in EVERY format
          // (time / bars.beats / samples) — no sync loss on format switch.
          setClock(formatPosition(st.projectSec, st.disp, st.label));
          const isLabel = st.disp ? st.disp.fmt !== 0 : st.label !== undefined;
          clockEl.classList.toggle("clock__value--label", isLabel);
        }
        {
          // Transport state drives the chip's text/colour; WHICH element is
          // visible (chip vs. "Pause DAW" button) is applyTransportUi's job.
          // innerHTML only on CHANGE (dataset compare) — not every frame.
          const playing = st.playing && st.hasSignal;
          const v = playing ? "on" : "off";
          transportEls.forEach((el) => {
            if (el.dataset.play === v) return;
            el.dataset.play = v;
            if (el instanceof HTMLButtonElement) el.innerHTML = `${ICON_PAUSE} Pause DAW`;
            else el.innerHTML = playing ? `${ICON_PLAY} PLAYING` : `${ICON_PAUSE} PAUSED`;
          });
          this.applyTransportUi(playing);
        }
        const latEl = this.root.querySelector<HTMLElement>("[data-latency]");
        if (latEl) latEl.textContent = `Buffer ${this.wsRingTargetMs()} ms`;

        // §14 talkback rule, see syncDawPlaying(): wanted mics mute while music
        // plays and reopen on stop.
        this.syncDawPlaying(st.playing && st.hasSignal);

        // Caption follows the AUDIBLE path every frame — a host quality switch
        // while the WS player stays active has no activation edge to hook on
        // (the old edge-triggered label stuck at "Opus 320 kbps" forever). True
        // lossless shows its bit depth; Opus (WS primary or RTC fallback) shows
        // the host-chosen bitrate — the WS frames ARE the plugin's packets, so
        // the plugin-reported bitrate is exact for both.
        {
          const bits = this.lossless?.isActive() ? (this.lossless?.bitDepth() ?? 0) : 0;
          const fmtEl = this.root.querySelector<HTMLElement>("[data-format]");
          if (fmtEl) {
            const f = bits > 0 ? "lossless" : "opus";
            if (fmtEl.dataset.format !== f) {
              fmtEl.dataset.format = f;
              fmtEl.textContent = bits > 0 ? "Lossless" : "Opus";
            }
          }
          const bitsEl = this.root.querySelector<HTMLElement>("[data-bits]");
          if (bitsEl) {
            const bps = st.disp?.bitrate ?? 0;
            const txt = bits > 0 ? `${bits}-bit` : `${bps > 0 ? Math.round(bps / 1000) : 256} kbps`;
            if (bitsEl.textContent !== txt) bitsEl.textContent = txt;
          }
        }
      }
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private refreshPlayButton(): void {
    const btn = this.root.querySelector<HTMLButtonElement>('[data-role="play"]');
    if (!btn) return;
    // Show the tap-to-play escape hatch when EITHER path is blocked: the
    // WebRTC element (autoplay) or a built-but-suspended WS graph.
    const needsGesture = this.room ? !this.room.canPlayback : true;
    const wsSilent = this.lossless?.isEnabledButSilent() ?? false;
    btn.hidden = !(needsGesture || wsSilent);
  }

  /** §14: show/hide the talk bar per the host's call promotion. On demotion the
   *  server already revoked publish rights (LiveKit unpublishes the tracks);
   *  we still switch our local toggles off for a clean UI state. */
  private setCallMode(on: boolean): void {
    // Visibility is enforced EVERY poll (idempotent) — a rebuilt shell or a
    // stale flag can never leave the bar stranded in the wrong state.
    this.root
      .querySelectorAll<HTMLElement>("[data-talkbar]")
      .forEach((el) => (el.hidden = !on));
    if (on === this.inCall) return;
    this.inCall = on;
    if (on) {
      this.micWanted = false;
      // Capture begins only after the guest explicitly activates their microphone.
      void this.fillTalkDevices();
      if (!this.isHost) this.toast("The host added you to the call - your mic is off until you turn it on.", {
        durationMs: 9000,
        strong: true,
      });
    } else {
      this.micWanted = false;
      this.releaseMic(); // demote: give the device back (see applyTalkState)
    }
    this.applyTalkState();
  }

  /** iOS un-stick: rebuild every audio context AFTER a real mic capture
   *  ended. WebKit leaves running contexts on the degraded voice output path
   *  even once the audio session returns to playback — only FRESH contexts
   *  pick up the restored hi-fi route. Costs one short re-prebuffer. */
  /** The output width this device shows when it is HEALTHY — measured once,
   *  before any capture has ever run. A phone whose speaker is genuinely mono
   *  must not be dragged through retry rebuilds forever. */
  private healthyOutChannels = 0;
  /** Field evidence that the rebuild really ran. */
  private rebuildsDone = 0;
  /** A room drop WE caused — the self-heal must not treat it as a failure. */
  private intentionalRoomDrop = false;
  /** A route reset is in flight: the room is (about to be) down, so no capture
   *  may be started until it is back. Without this the hands-free mic reopens
   *  at a transport stop that lands inside the gap and LiveKit rejects the
   *  publish — "Microphone could not be started (track error)", 21.08. */
  private routeResetting = false;
  /** One rebuild in flight at a time (the watchdog fires every second). */
  private rebuildPending = false;

  private async rebuildAudioPipeline(): Promise<void> {
    const isTouch =
      (typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches) ||
      (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0);
    if (!isTouch) return; // desktop output is never degraded — no gap for nothing
    // Never rebuild while a capture is (about to be) live again — and re-arm,
    // or a rebuild skipped here would be skipped for good (the watchdog only
    // fires on micCaptureUsed, which the caller has already cleared).
    const shouldSend = this.inCall && this.micWanted && !this.dawPlaying;
    if (shouldSend || this.micEnabling) {
      this.micCaptureUsed = true;
      return;
    }
    this.setAudioSessionType("playback");
    const l = this.lossless;
    if (l) {
      this.room?.setSharedAudioContext(null); // the old ctx is about to die
      await l.disable();                      // closes ctx + socket
      // The rebuild that has worked for this host before: a fresh context,
      // built once the capture is really gone. On top of it, a real silence —
      // the WebRTC elements used to play straight through the gap and keep the
      // audio session alive, which matters on Bluetooth, where the capture
      // drags the sink down to the mono HFP link and iOS only renegotiates
      // when nothing is playing. (Never fairly tested: the single run that
      // reached this code did so while the SOURCE was hard-left, so it
      // measured mono no matter what.)
      const parked = this.room?.parkAudioElements() ?? [];
      this.routeResetting = true;
      this.setAudioSessionType("playback");
      // NO room teardown here. Dropping the LiveKit room did silence the
      // audio session properly — and cost the listener their microphone: the
      // publish right lives on the CONNECTED participant, not in the token
      // (see mint_join_ok), so reconnecting came back without it and the next
      // mic tap died with PublishTrackError. Bluetooth staying on the mono
      // link is the smaller loss; it is recovered by closing and reopening the
      // browser, and over the phone speaker this rebuild restores stereo on
      // its own.
      await new Promise((r) => window.setTimeout(r, App.BT_ROUTE_SETTLE_MS));
      this.losslessStartAttempted = false;
      await this.ensureLossless();            // fresh ctx + fresh /lossless socket
      this.rebuildsDone += 1;
      if (this.room) this.room.resumeAudioElements(parked);
      this.routeResetting = false;
      // The transport may have stopped while the room was down: re-derive, so
      // a hands-free mic that wanted to open during the gap opens now.
      this.applyTalkState();

    } else {
      this.room?.refreshConversationRouting();
    }
  }

  /** Unpublish + STOP every local audio track (mic). Stopping releases the
   *  capture device — required on iOS to leave the "call" audio session.
   *  A second sweep runs shortly after: a setMicrophoneEnabled(true) that was
   *  still in flight when the state flipped publishes AFTER the first sweep
   *  and would silently re-open the device for the whole playthrough. */
  /** iOS-like device (incl. iPadOS masquerading as macOS)? The capture
   *  lifecycle differs fundamentally: iOS must RELEASE the device on every
   *  mute (audio-session fix), desktop keeps it and merely mutes. */
  private isIosLike(): boolean {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  }

  private releaseMic(): void {
    const sweep = () => {
      const lp = this.room?.room.localParticipant;
      if (!lp) return;
      for (const pub of lp.audioTrackPublications.values()) {
        if (pub.track) void lp.unpublishTrack(pub.track, true).catch(() => {});
      }
    };
    sweep();
    this.setAudioSessionType("playback"); // snap the output back to hi-fi
    window.setTimeout(() => {
      // Same rule as applyTalkState: private monitoring keeps mics open.
      const shouldSend = this.inCall && this.micWanted && !this.dawPlaying;
      if (!shouldSend) {
        sweep();
        this.setAudioSessionType("playback");
      }
    }, 1500);
  }

  /** Start the mic capture/publish. ONE enable at a time: the permission
   *  prompt keeps the promise pending for as long as the user thinks — that is
   *  fine and NOT a failure (a premature retry used to read LiveKit's
   *  duplicate-call "undefined" as an error and flipped the mic off right
   *  before the user's "Allow" landed). Afterwards re-derive: if the DAW
   *  started playing during the prompt, the fresh track is released again. */
  private startMicCapture(lp: LocalParticipant): void {
    this.micEnabling = true;
    this.setAudioSessionType("auto"); // a pinned "playback" category cannot record
    // NO output fade here any more. It hid the route-change crackle, but it
    // blanked ~1.1 s of output at the very moment the mic re-arms after a
    // STOP — swallowing the buffered tail and letting the remainder reappear
    // afterwards: "the music stops at once, then after a short pause a
    // leftover tail arrives" (field report 19.08., iPhone and iPad, only
    // while in the call). A crackle is the smaller evil than chopping the end
    // off the material.
    // Pin the PICKER's device at the moment of enabling. Preselecting at
    // list-fill time raced the room setup (and a fill before the room exists
    // no-ops), so the FIRST capture could still grab the browser default —
    // "the mic only works after switching away and back".
    const sel = this.root.querySelector<HTMLSelectElement>("[data-talk-mic-sel]");
    const want = sel?.value || null;
    const pinned = want
      ? this.room!.room.switchActiveDevice("audioinput", want).catch(() => {})
      : Promise.resolve();
    void pinned
      .then(() => lp.setMicrophoneEnabled(true))
      .then((pub) => {
        this.micEnabling = false;
        if (pub) {
          this.micCaptureUsed = true; // a real capture ran on this page
          // NO talk gate in the publish path (2026-08-14): its own
          // AudioContext could start suspended (autoplay policy — the attach
          // runs async, outside the click), and a suspended graph publishes
          // SILENCE while the level meter (raw track) still shows life — the
          // host "could not talk" with every mic on the machine. Until a
          // provably fail-open version exists, anti-feedback = talk duck
          // (voices) + echo cancellation + headphones. talkgate.ts stays for
          // that future version.
          this.applyTalkState();
          return;
        }
        // Resolved without a publication (another call was in flight):
        // re-check in a moment; the state machine converges on its own.
        window.setTimeout(() => this.applyTalkState(), 1000);
      })
      .catch((e: unknown) => {
        // A REAL rejection: be honest — and SAY WHY (permission vs device
        // vs publish/network failure need entirely different fixes).
        this.micEnabling = false;
        const err = e as { name?: string; message?: string } | undefined;
        const why = err?.name || err?.message || "unknown error";
        console.error("mic enable failed:", e);
        // A publish that failed because the room was momentarily down (route
        // reset, reconnect) is a timing accident, not a refusal: keep the
        // listener's intent and try again once the room is back, instead of
        // switching their mic off and blaming them for it.
        if (this.routeResetting || !(this.room?.isConnected ?? false)) {
          window.setTimeout(() => this.applyTalkState(), 1200);
          return;
        }
        if (this.micWanted) {
          this.micWanted = false;
          this.applyTalkState();
          this.toast(`Microphone could not be started (${why}).`);
        }
      });
  }

  /** §14: derive + apply the actual mic state from intent × DAW transport, and
   *  paint the three-state button (off / live / automuted). Idempotent. */
  private applyTalkState(): void {
    const lp = this.room?.room.localParticipant;
    // NOT gated on streamPaused any more: while the host monitors privately
    // (listeners muted, timecode hidden) the microphones must stay OPEN —
    // that is the whole point of the mode, everyone keeps talking while the
    // host scrubs around unheard. There is no music to feed back into either.
    const shouldSend = this.inCall && this.micWanted && !this.dawPlaying;
    const state: "off" | "live" | "automuted" =
      !this.micWanted ? "off" : shouldSend ? "live" : "automuted";
    // The auto-mute runs both ways on every platform again: the mic closes
    // when the music starts and reopens when the transport stops, hands-free
    // (host, 21.08.: "dass ich nicht am iphone jedes mal manuell schalten
    // muss"). The §21 one-turn-per-tap rule was a workaround for the mono, and
    // the mono is dealt with where it happens now — the route reset in
    // rebuildAudioPipeline. On iOS the capture DEVICE is still released on
    // every auto-mute; that part is an audio-session necessity, not a policy.

    // Closing the mic ends any loop risk instantly — snap the duck fully
    // open rather than easing (the ease is for mid-conversation pumping).
    if (state !== "live" && this.duckFactor !== 1) {
      this.duckFactor = 1;
      this.applyOutputVolume();
    }
    if (state !== "live" && this.micArmTimer !== null) {
      window.clearTimeout(this.micArmTimer);
      this.micArmTimer = null;
    }
    if (lp) {
      if (state === "off" || (state === "automuted" && this.isIosLike())) {
        // iOS (and explicit OFF everywhere): release the capture DEVICE, not
        // just the sending — iOS keeps the phone in its ducked, floor-limited
        // "call" audio session as long as ANY capture is open. The release's
        // brief route change lands in the play-edge resync gap.
        this.releaseMic();
        // iOS UN-STICK: audio contexts that lived through a capture keep the
        // degraded voice OUTPUT even after the session returns to playback —
        // field-confirmed ("stays bad until reload"). Rebuild the pipeline on
        // fresh contexts once the device is free; the short re-prebuffer gap
        // lands right where the resync gap already is.
        // Rebuild the chain whenever a capture actually ran — NOT only on an
        // explicit mic-off. Removing it (19.08.) traded the play-edge fade-in
        // for MONO: on iOS a context that lived through a capture stays in the
        // voice-processing mode, which is single-channel, and the 1 Hz session
        // re-pin does not undo that. Stereo is the product; a rough start is
        // not worth losing it.
        //
        // TIMING, learned twice: it must run AFTER the capture is really gone.
        // Moving it to the transport edge (19.08.) made it useless — iOS is
        // still in the voice-processing session at that instant, so the fresh
        // context was born mono all over again ("after joining the call once
        // it is mono again"). The delay is what lets the OS return to the
        // playback session first. The fade-in this move was meant to cure had
        // a different cause anyway: the /lossless socket was being killed
        // seconds after opening (fixed separately), which is what made the
        // music arrive thinly.
        // NOT scheduled here any more. Hanging it off the talk-state EDGE
        // meant it had to win a race against the capture start, and the field
        // log of 21.08. shows it losing: "talk=off cap=1 rb=0/0" for a solid
        // minute — micCaptureUsed was set AFTER the state had already flipped
        // to off, so this condition was false and no rebuild ever ran. Every
        // mono report since then was this, not the theory of the week. The
        // watchdog now triggers on the STATE (a capture ran, nothing captures
        // now), which cannot be raced.
      } else if (state === "automuted") {
        // DESKTOP auto-mute: keep the capture alive, stop only the SENDING
        // (LiveKit keeps the track and flags it muted). Releasing the device
        // here — an iOS necessity, see above — cost desktop hosts 5-6 s of
        // re-arm (permission plumbing, re-publish, re-subscribe) before they
        // could be heard after every stop.
        if (lp.isMicrophoneEnabled && !this.micEnabling) {
          void lp.setMicrophoneEnabled(false).catch(() => {});
        }
      } else if (!lp.isMicrophoneEnabled && !this.micEnabling && this.micArmTimer === null) {
        // Desktop with a kept (muted) track: un-muting is a flag flip — no
        // device grab, no route change, no re-subscribe. Do it NOW; the
        // debounce below exists for the RELEASE path's route flips.
        const kept = !this.isIosLike() && lp.audioTrackPublications.size > 0;
        if (kept) {
          this.micEnabling = true;
          void lp
            .setMicrophoneEnabled(true)
            .then(() => {
              this.micEnabling = false;
              this.applyTalkState();
            })
            .catch(() => {
              this.micEnabling = false;
            });
        } else if (this.lastTalkState === "automuted") {
          // Coming out of a transport pause: re-arm DEBOUNCED. Two reasons:
          // (1) scrubbing / quick play-pause cycles must not flip the audio
          // session every time (on Bluetooth each flip is an audible A2DP/HFP
          // change); (2) the transport edge OUTRUNS the buffered audio by the
          // latency setting — opening the mic immediately ripped the route
          // while the last [latency] ms of music were still playing out (the
          // "tiny glitch on remote pause"). Wait until the tail is done.
          // Just long enough for the buffered tail to finish playing (the
          // route change must not land in the music) — pausing usually means
          // "I want to say something NOW", so no flat second on top.
          const tailMs =
            this.appliedLatencyMs > 0 ? this.appliedLatencyMs : DEFAULT_PLAYOUT_DELAY_MS;
          this.micArmTimer = window.setTimeout(() => {
            this.micArmTimer = null;
            this.applyTalkState();
          }, Math.max(300, tailMs + 150));
        } else if (!this.routeResetting) {
          this.startMicCapture(lp);
        }
      }
    }
    const btn = this.root.querySelector<HTMLButtonElement>("[data-talk-mic]");
    if (btn) {
      btn.dataset.talkMic = state;
      if (this.isHost) { btn.setAttribute("aria-label", this.micWanted ? "Turn microphone off" : "Turn microphone on"); btn.setAttribute("aria-pressed", String(this.micWanted)); }
    }
    // Icons carry the normal states; only the SURPRISING one gets words.
    if (btn) {
      btn.title =
        state === "off"
          ? "Your microphone is off - click to talk"
          : state === "live"
            ? "Your microphone is live - click to switch it off"
            : "Muted automatically while the DAW is playing";
    }
    const hint = this.root.querySelector<HTMLElement>("[data-talk-hint]");
    if (hint) {
      hint.hidden = state !== "automuted";
      // Short on purpose: the row must never have to truncate it.
      hint.textContent = this.isHost ? state === "live" ? "Guests can hear you." : state === "automuted" ? "Muted while music plays." : "Guests cannot hear you." : "auto-muted";
    }
    this.lastTalkState = state;
  }

  private async fillTalkDevices(): Promise<void> {
    const fill = async (kind: MediaDeviceKind, sel: HTMLSelectElement | null) => {
      if (!sel) return;
      try {
        const devs = await LKRoom.getLocalDevices(kind, false);
        if (devs.length === 0) return;
        const active = this.room?.room.getActiveDevice(kind);
        // NO "System" pseudo-entry, on any platform. It was a different
        // surprise everywhere: iOS does not enumerate the alias at all, and on
        // macOS the browser's OWN default-mic preference (independent of the
        // OS setting!) could point at a silent device — "Mic: System" then
        // captured nothing while the very same physical mic worked when
        // picked by name. Concrete devices only: what the picker says is what
        // captures. The alias is used once, to PRESELECT the device behind
        // the system default.
        const def = devs.find((d) => d.deviceId === "default");
        const real = devs.filter(
          (d) => d.deviceId !== "default" && d.deviceId !== "communications",
        );
        if (real.length === 0) return;
        sel.innerHTML = "";
        real.forEach((d, i) => {
          const o = document.createElement("o" + "ption") as HTMLOptionElement;
          o.value = d.deviceId;
          o.textContent = d.label || `Mic ${i + 1}`;
          sel.appendChild(o);
        });
        const defaultTwin =
          def && real.find((d) => d.groupId === def.groupId)?.deviceId;
        if (active && active !== "default" && real.some((d) => d.deviceId === active)) {
          sel.value = active;
        } else {
          sel.value = defaultTwin ?? real[0]!.deviceId;
          // Make LiveKit capture the PRESELECTED device too — otherwise the
          // first enable still grabbed the browser-default (the silent one).
          void this.room?.room.switchActiveDevice(kind, sel.value).catch(() => {});
        }
      } catch {
        /* enumeration blocked — selects stay as-is */
      }
    };
    await fill("audioinput", this.root.querySelector<HTMLSelectElement>("[data-talk-mic-sel]"));
  }

  /** §12: swap the play/pause STATUS badge for the actionable pause BUTTON when
   *  the host grants remote pause (one element at a time — never both). */
  /** §12: does this listener may pause the host's DAW (roster grant)? */
  private remotePauseAllowed = false;

  private setRemotePauseAllowed(allowed: boolean): void {
    this.remotePauseAllowed = allowed;
    this.applyTransportUi(this.dawPlaying);
  }

  /** ONE transport element at a time — status and action never double up:
   *    no grant           → the chip (PLAYING / PAUSED)
   *    grant + playing    → the red "Pause DAW" button (a pause button IS the
   *                         "it is playing" signal; the clock runs too)
   *    grant + paused     → the chip (PAUSED) — starting is the host's move
   *  Called from the render loop on every transport edge. */
  private applyTransportUi(playing: boolean): void {
    const chip = this.root.querySelector<HTMLElement>("span.transport-chip");
    const btn = this.root.querySelector<HTMLButtonElement>("[data-remote-pause]");
    const showButton = this.remotePauseAllowed && playing && !this.streamPaused;
    if (chip) chip.hidden = showButton;
    if (btn) {
      btn.hidden = !showButton;
      btn.disabled = !showButton;
    }
  }

  private setListenerCount(count: number): void {
    const el = this.root.querySelector<HTMLElement>("[data-listeners]");
    if (el) el.textContent = String(count);
  }

  private setConnState(connected: boolean): void {
    const el = this.root.querySelector<HTMLElement>("[data-conn]");
    if (el) {
      el.dataset.conn = connected ? "on" : "off";
      el.textContent = connected ? "LIVE" : "CONNECTING\u2026";
    }
    this.refreshPlayButton();
  }

  /** §27: the DAW-playing edge, and everything that hangs off it. Extracted
   *  from the render loop because that loop is requestAnimationFrame — which
   *  browsers STOP in a background tab. With the deck behind another tab the
   *  host's transport could start and this was never re-derived: the mic
   *  stayed open right through the playback ("gestern war wohl während eines
   *  calls beim playback mein mikrofon offen", 22.08.). The 1 Hz watchdog and
   *  the visibility hook call it too, so a hidden tab is at most a second
   *  behind instead of indefinitely wrong. */
  private syncDawPlaying(daw: boolean): void {
    if (daw === this.dawPlaying) return;
    this.dawPlaying = daw;
    this.applyTransportUi(daw);
    if (this.inCall) this.applyTalkState();
    // Play EDGE: REAPER prefills its engine in a fast burst that would
    // otherwise stack onto the ring as standing latency. Re-prebuffer to the
    // receiver target instead — the burst fills it in well under real time, so
    // playback starts clean AND at the chosen latency.
    if (daw) this.lossless?.resync();
  }

  private setMixState(live: boolean): void {
    const bar = this.root.querySelector<HTMLElement>('[data-role="waitbar"]');
    if (bar) bar.hidden = live;
    this.mixLive = live;
    if (live) {
      this.everStreamed = true;
      // Source presence does not override the independent private-monitoring gate.
    }
    // The host started again: reconnect the audio socket IMMEDIATELY instead
    // of waiting out whatever backoff the previous stop had grown.
    if (live) {
      this.lossless?.kick();
      void this.room?.ensureAudioPlayback();
    }
    // Reuse the connection badge intent; if mix drops, indicate waiting.
    if (!live) {
      const el = this.root.querySelector<HTMLElement>("[data-conn]");
      if (el && this.room?.isConnected) {
        el.dataset.conn = "wait";
        el.textContent = "WAITING FOR STREAM\u2026";
      }
    } else {
      this.setConnState(this.room?.isConnected ?? false);
    }
    this.refreshPlayButton();
  }

  // -------------------------------------------------------------------------
  // Error / toast / lifecycle
  // -------------------------------------------------------------------------

  /** Friendly pre-stream screen: the session id in the link has no live session
   *  yet (host has not pressed "Start streaming"). Polls until it appears, then
   *  connects automatically — listeners can open the link ahead of time. */
  /** §24 waiting room. The complaint it answers: "man sitzt gefühlt schon da
   *  und wartet, dass es endlich losgeht, weil die klare Botschaft fehlt, dass
   *  die Session noch gar nicht gestartet ist" (host, 22.08.). So this page
   *  says exactly that — and nothing that looks live: no transport, no mixer,
   *  no microphone. This client is in no room at all until the host opens. */
  private renderWaitingRoom(sessionName: string, others: string[], oneByOne: boolean): void {
    this.setView("waiting");
    this.root.innerHTML = "";
    this.root.appendChild(this.headerEl());
    const card = document.createElement("main");
    card.className = "card card--center";

    const h = document.createElement("h1");
    h.className = "card__title";
    h.textContent = "The session hasn't started yet";

    const p = document.createElement("p");
    p.className = "card__sub";
    p.textContent = oneByOne
      ? `You're in the waiting room for "${sessionName}". The host lets people in one by one — this page continues by itself.`
      : `You're in the waiting room for "${sessionName}". It continues by itself as soon as the host opens the session.`;
    card.append(h, p);

    // Who else is here. A waiting room with names in it feels like a room;
    // an empty one at least tells you that you are the first.
    const who = document.createElement("p");
    who.className = "card__sub waitroom__who";
    const all = [this.displayName, ...others].filter(Boolean);
    who.textContent =
      all.length > 1
        ? `Waiting: ${all.join(", ")}`
        : `Waiting: you (${this.displayName})`;
    card.appendChild(who);
    this.root.appendChild(card);

    // Keep knocking. The very same call returns the token the moment the host
    // opens — no separate endpoint, no state to keep in sync — and each knock
    // refreshes this listener's presence, so the host sees them stay.
    if (this.waitTimer !== null) window.clearInterval(this.waitTimer);
    this.waitTimer = window.setInterval(() => {
      if (this.pollBusy.waiting) return;
      this.pollBusy.waiting = true;
      void joinSession(this.sessionId, this.displayName, this.clientId)
        .finally(() => (this.pollBusy.waiting = false))
        .then((r) => {
          if (r.status === "waiting") {
            // Refresh the list of fellow waiters without rebuilding the page.
            const names = [this.displayName, ...r.others].filter(Boolean);
            const el = this.root.querySelector<HTMLElement>(".waitroom__who");
            if (el)
              el.textContent =
                names.length > 1
                  ? `Waiting: ${names.join(", ")}`
                  : `Waiting: you (${this.displayName})`;
            return;
          }
          if (this.waitTimer === null) return; // another tick got there first
          window.clearInterval(this.waitTimer);
          this.waitTimer = null;
          void this.handleJoinResult(r);
        })
        .catch(() => {
          /* transient — keep knocking */
        });
    }, 3000);
  }

  private renderWaitingForStream(): void {
    this.setView("waiting");
    this.root.innerHTML = "";
    this.root.appendChild(this.headerEl());
    const card = document.createElement("main");
    card.className = "card card--center";
    const h = document.createElement("h1");
    h.className = "card__title";
    h.textContent = "Not streaming yet";
    const p = document.createElement("p");
    p.className = "card__sub";
    p.textContent =
      "This page will connect automatically as soon as the stream starts. " +
      "(If the host restarted the session, ask for a fresh link.)";
    card.append(h, p);
    this.root.appendChild(card);
    this.waitTimer = window.setInterval(() => {
      if (this.pollBusy.waiting) return; // REVIEW 2.3: one in flight
      this.pollBusy.waiting = true;
      void getSession(this.sessionId)
        .finally(() => (this.pollBusy.waiting = false))
        .then((s) => {
          if (!s.live) return; // session exists but the stream is not up yet
          // Only the tick that actually clears the timer may start() — two
          // in-flight polls used to both fire start() (double room/audio).
          if (this.waitTimer === null) return;
          window.clearInterval(this.waitTimer);
          this.waitTimer = null;
          void this.start();
        })
        .catch(() => {
          /* still no session — keep waiting */
        });
    }, 4000);
  }

  private renderError(message: string): void {
    this.setView("error");
    this.root.innerHTML = "";
    this.root.appendChild(this.headerEl());
    const card = document.createElement("main");
    card.className = "card card--center";
    const h = document.createElement("h1");
    h.className = "card__title";
    h.textContent = "Something went wrong";
    const p = document.createElement("p");
    p.className = "card__sub";
    p.textContent = message;
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "btn btn--primary";
    retry.textContent = "Try again";
    retry.addEventListener("click", () => {
      retry.disabled = true; // double-click = two concurrent start() runs
      void this.start();
    });
    card.append(h, p, retry);
    this.root.appendChild(card);
  }

  private toast(message: string, opts?: { durationMs?: number; strong?: boolean }): void {
    let t = this.root.querySelector<HTMLElement>(".toast");
    if (!t) {
      t = document.createElement("div");
      t.className = "toast";
      this.root.appendChild(t);
    }
    t.textContent = message;
    t.classList.toggle("toast--strong", opts?.strong === true);
    t.classList.add("toast--show");
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(
      () => t?.classList.remove("toast--show"),
      opts?.durationMs ?? 4000,
    );
  }

  private setView(v: View): void {
    this.root.dataset.state = v;
  }

  private errMsg(e: unknown, fallback: string): string {
    if (e instanceof ApiError) return e.message || fallback;
    if (e instanceof Error) return e.message || fallback;
    return fallback;
  }

  /** Tear down (e.g. on page unload). */
  async destroy(): Promise<void> {
    await this.teardownLive();
  }

  /** Release all live-session resources and reset the lossless latch, so the
   *  instance can be re-used for a clean re-join. Safe to call when nothing is
   *  live (all fields optional). */
  private async teardownLive(): Promise<void> {
    this.devicesAbort?.abort(); this.devicesAbort = null;
    if (this.rafHandle !== null) { cancelAnimationFrame(this.rafHandle); this.rafHandle = null; }
    if (this.healthTimer !== null) { window.clearInterval(this.healthTimer); this.healthTimer = null; }
    if (this.ctxWatchTimer !== null) { window.clearInterval(this.ctxWatchTimer); this.ctxWatchTimer = null; }
    this.duckFactor = 1;
    this.ctxStalled = false;
    this.unhookGestureLatch?.();
    this.unhookGestureLatch = null;
    // Talk state must not survive into the next live session: a stale
    // micWanted would re-arm the mic after an auto-reconnect while the fresh
    // button shows "Mic off".
    if (this.micArmTimer !== null) { window.clearTimeout(this.micArmTimer); this.micArmTimer = null; }
    this.inCall = false;
    this.micWanted = false;
    this.dawPlaying = false;
    this.micEnabling = false;
    this.lastTalkState = "off";
    this.stopPolling();
    if (this.commentSyncTimer !== null) { window.clearInterval(this.commentSyncTimer); this.commentSyncTimer = null; }
    this.discussion?.destroy(); this.discussion = null;
    this.communicationOpen = false;
    this.hostControls?.destroy();
    this.hostControls = null;
    this.comments?.destroy();
    this.comments = null;
    const lossless = this.lossless;
    this.lossless = null;
    this.losslessStartAttempted = false;
    await lossless?.disable();
    const room = this.room;
    this.room = null;
    await room?.disconnect();
    this.streamPaused = false;
  }
}

// ---------------------------------------------------------------------------
// Small DOM helpers
// ---------------------------------------------------------------------------

function labeledInput(label: string, type: string, placeholder: string) {
  const wrap = document.createElement("label");
  wrap.className = "field";
  const span = document.createElement("span");
  span.className = "field__label";
  span.textContent = label;
  const input = document.createElement("input");
  input.type = type;
  input.className = "field__input";
  input.placeholder = placeholder;
  wrap.append(span, input);
  return { wrap, input };
}

function showErr(el: HTMLElement, msg: string): void {
  el.textContent = msg;
  el.hidden = false;
}
